/**
 * CityAgents — lado "cliente" da dinâmica do jogo: a máquina de estados dos
 * avatares e a navegação pela cidade. É dirigido por mensagens do backend
 * (real via websocket ou o SimulatedBackend), e emite a posição de cada avatar
 * a cada tick para o renderer.
 *
 * Ciclo de vida de um avatar:
 *   entering  → entra por uma das 3 vias laterais e vai até a 1ª interseção
 *   wandering → anda aleatoriamente pelo grafo de ruas
 *   traveling → backend mandou um edifício; roteia até lá e estaciona na vaga
 *   parked    → estacionado na quadra (frente→lateral→perto→fundos); executa as
 *               "ações" (hook placeholder) até o backend mandar concluir
 *   leaving   → backend parou de enviar (ou mandou sair): vai embora por uma via
 *
 * Protocolo (backend → cliente): spawn / goto / actions / complete / leave.
 * Eventos (cliente → backend): 'wandering' | 'parked' | 'exited'.
 */
import { probes, P } from '../perfProbes.js';
import {
  acquireCarSlot,
  acquireCarSlotNear,
  releaseCarSlot,
  acquirePersonSlot,
  acquirePersonSlotNear,
  releasePersonSlot,
  buildingPoint,
  BUILDING_COUNT,
  CAR_CURB,
  nearestCarCurb,
  personSpotGate,
  personSpotExit,
  carPortals,
  portalPoint,
  PORTAL_SPAN, personPortalExit
} from '../world/Parking.js';
import { buildingsAreNeighbors, buildingNeighborsPlaza, insideDistrict, DISTRICTS, PLAZA, SIDEWALK, BUILDINGS, BUILDING_IDS, strollDistrictOf } from '../world/cityLayout.js';
import { ROADS, isDrivable } from '../config.js';

// PISTA = só as vias DIRIGÍVEIS. O eixo interno de uma superquadra continua em `ROADS`
// (é ele que define a grade de quadras), mas virou calçada/parque: para o CARRO ele não
// existe — não entra no grafo, não vira vaga, não conta como "estar na rua" e a trava de
// segurança nunca projeta ninguém nele. Ver config.isDrivable.
const DRIVE_V = ROADS.vertical.filter(isDrivable);
const DRIVE_H = ROADS.horizontal.filter(isDrivable);

// ZONA DE TRANSFORMAÇÃO do portal (modo sem colisão carro-carro): o ponto nominal é
// referência — o carro pode transformar até PORTAL_TOL antes/depois dele ao longo do
// meio-fio, desde que dentro do segmento útil do portal (que já tem margem das
// esquinas), na banda da rua adjacente e FORA do centro do cruzamento.
const PORTAL_TOL = 12;
const TRANSFORM_DUR = 0.8; // s — avatar fica parado "girando" enquanto troca carro↔pessoa
// A pessoa recém-desembarcada aparece ONDE O CARRO ENCOSTOU no meio-fio. O antigo
// PERSON_NEAR_MAX (descartar a vaga que estivesse longe, p/ a pessoa não "voar") saiu junto
// com a lista finita de vagas: a coroa contínua sempre devolve uma posição PERTO do carro, e
// quando não está, a pessoa ANDA até lá pelo portão em vez de teleportar.
const PERSON_SLIDE_MAX = 6.5;  // se a calçada está até aqui do meio-fio, o GIRO desliza a pessoa
// até lá (termina na calçada, não parada girando na rua); mais longe (faixa oposta), ela ANDA.
const rnd = (lo, hi) => lo + Math.random() * (hi - lo);

const LAT_DECAY = 0.82;
const MARGIN = 1.6;        // folga extra entre pedestres (mais espaço, sem sobrepor)
const CELL = 8;
const FOLLOW_BUFFER = 1.2; // gap de "fila" entre carros (mais espaço, sem matar a vazão)
// FREIO SUAVE (headway): a anticolisão limita a velocidade a `gap / HEADWAY` em vez
// de "bater na parede" do buffer. O carro desacelera PROGRESSIVAMENTE conforme se
// aproxima e fica em micro-movimento (assintótico) — nunca congela de vez nem dá
// freada seca; retoma na hora quando o gap abre. MESMA geometria de segurança de
// antes (os pontos de parada não mudam) → zero risco novo de sobreposição.
const HEADWAY = 0.35; // s — tempo de "distância de seguimento" (menor = mais agressivo)

// Manobra de meia-volta (baliza): quando a curva é fechada demais para o raio
// de viragem caber na rua, o carro manobra de frente/ré em vez de subir na
// calçada.
const UTURN_THRESHOLD = 2.4;   // ~137°: só dispara em meia-volta (não em 90°)
const MANEUVER_SPEED = 3.5;    // devagar, como manobrando
const MANEUVER_YAW = 1.6;      // rad/s — esterço apertado
const CORRIDOR_HALF = 2.2;     // quanto o centro do carro pode afastar do eixo da via
const MANEUVER_MAX_TICKS = 60; // trava de segurança: nunca fica preso manobrando

// Quebra-deadlock: carro travado tempo demais (anti-colisão prendeu) é LIBERADO
// — força passagem (ignora carros, mas nunca atropela pedestres) p/ desfazer o
// congestionamento na hora.
const FORCE_TICKS = 16;   // ~2 s travado (tickMs 120) → libera
const FORCE_SPEED = 4.0;  // empurrão lento p/ furar o engarrafamento

// SAÍDA garantida (SEM sumir na tela). O backend removeu o jogador (timeout) e mandou
// 'leave' — o avatar dirige até a BORDA e só é removido quando SAI DE FATO pela via
// lateral (some off-screen), NUNCA no meio da cidade. Por que NÃO um timer que mata no
// lugar: no congestionamento a maioria não chega à borda em 20s (medido: ~107/150), e um
// timer os APAGAVA no meio do mapa — o bug que o usuário reportou. Agora eles seguem
// visíveis dirigindo p/ fora (o quebra-deadlock garante que o CARRO sempre avança).
//   • EXIT_GONE_*: cruzou a borda (off-screen) → remove. Os pontos de saída ("off") das
//     vias ficam em |x|=157 / |z|=152; estes limiares (um pouco antes) pegam a saída de
//     forma robusta, bem além das quadras (|x|<120 / |z|<74) — sempre fora de vista.
//   • LEAVE_STUCK_S: rede de segurança por FALTA DE PROGRESSO (não por tempo total): um
//     avatar SAINDO que não anda de fato (travou de vez — ex.: pedestre encurralado indo à
//     rua, ou giro preso; o carro tem o quebra-deadlock) por muito tempo é removido. NÃO
//     dispara em quem anda devagar no trânsito (esse acumula deslocamento e zera o relógio)
//     — só no que está genuinamente CONGELADO (o "fantasma" que o timeout do backend já
//     tirou). Cobre TODOS os estados de saída (carro/pessoa/transformando).
const EXIT_GONE_X = 150, EXIT_GONE_Z = 145; // |pos| além disto (saindo) = off-screen → remove
const LEAVE_STUCK_S = 30;    // s SEM deslocar ≥ LEAVE_STUCK_EPS → travado de vez → remove
const LEAVE_STUCK_EPS = 2.0; // deslocamento (u) que conta como "andou" (zera o relógio de travado)
// CARRO PLANTADO (ver _vigiarCarroTravado). O vigia do trânsito olha VELOCIDADE; este olha
// DESLOCAMENTO, que é a única forma de enxergar o carro que "acha" que anda e não sai do
// lugar. Limiar folgado: a 10 u/s um carro cobre 0,5 u em 0,05 s, então só quem está de
// fato plantado chega perto disto.
const CAR_STUCK_S = 1.5;     // s querendo andar sem deslocar → destrava
const CAR_STUCK_EPS = 0.5;   // deslocamento (u) que conta como "andou"
const CAR_STUCK_V = 1.0;     // abaixo disto ele está freando no trânsito (é o outro vigia)
// PINO NA QUINA (superquadra compactada): o carro quer virar na esquina, o CORPO (raio) raspa a
// quina do distrito e o clamp o traz de volta todo quadro — um ciclo-limite que o vigia de
// deslocamento simples (ref único, CAR_STUCK_EPS) não pega, pois a oscilação zera o ref a cada volta.
// Detecta por PROGRESSO (robusto a oscilação E deriva ao longo da via): se o carro não se APROXIMA
// do waypoint enquanto o corpo raspa o distrito, está preso → solta na malha em <1s.
const PIN_PROGRESS = 1.5;    // u que precisa se aproximar do waypoint por período; menos que isso = preso
const PIN_S = 0.6;           // s raspando a quina sem progredir p/ o waypoint → destrava (<1s)
const PIN_PUSH = 6;          // u/s de afastamento lateral da quina (ver _afastarDaQuina):
                             // contínuo e imperceptível, no lugar do antigo salto p/ o nó
/**
 * MEIA-LARGURA RENDERIZADA do carro — o que o público de fato vê invadir a calçada.
 *
 * O `a.radius` (2.6) é raio de COLISÃO/espaçamento, quase o dobro disto, e usá-lo no teste
 * de invasão de superquadra acusava carro dirigindo NORMALMENTE. A conta, na Mexican: a via
 * central ocupa x∈[−5,5] e a superquadra começa exatamente em x=±5; o carro anda na faixa
 * da direita (laneBase 2.7, latClamp 1.2 → até 3.9 do eixo). Com 2.6 a "borda" dava 6.5 —
 * 1,5u dentro do distrito — para um carro cujo desenho termina em 5,2. Resultado: o vigia
 * de pino disparava o tempo todo nas vias centrais (medido: os focos de raspagem ficavam
 * todos sobre x=0 e z=0), e era isso que alimentava os teleportes.
 *
 * 2.06 (para-choque) × 1.25 (CAR_SCALE, ver Fleet) / 2 ≈ 1.29 — o mesmo número que o
 * CAR_CURB do Parking usa para encostar o carro no meio-fio sem subir nele.
 */
const CAR_BODY_HALF = 1.3;
// Correção máxima do clamp por quadro (ver o uso em _update). Bem acima de qualquer passo
// legal do carro (~1,8u a 15 u/s), então só as correções ANORMAIS — as que apareceriam
// como salto — são espalhadas em alguns quadros.
const CLAMP_MAX_STEP = 2.0;
/** O CORPO do carro (raio r) toca/entra em algum distrito? Distância do centro ao retângulo do
 *  distrito < r — pega inclusive a QUINA na diagonal (que os pontos axiais x±r/z±r não enxergam). */
function bodyOverlapsDistrict(x, z, r) {
  for (const d of DISTRICTS) {
    const L = d.land;
    const dx = Math.max(L.left - x, 0, x - L.right);
    const dz = Math.max(L.top - z, 0, z - L.bottom);
    if (dx * dx + dz * dz < r * r) return true;
  }
  return false;
}

// CURVAS (mão americana): o RAIO depende do SENTIDO, p/ a curva sair natural e DENTRO
// da faixa. À DIREITA o carro fecha na quadra de dentro (raio menor); à ESQUERDA ele
// cruza o cruzamento até a faixa do outro lado (raio maior). O carro segue reto na
// faixa até o ponto de tangência de ENTRADA do arco e então descreve o arco (raio fixo,
// ω=v/R) até tangenciar a faixa de SAÍDA — aí o seguimento de faixa reassume.
// LIMITE GEOMÉTRICO da curva à DIREITA: o ponto de tangência de entrada fica a
// (laneBase+R) atrás do nó; se passar de meia-via (ROADS.width/2) o arco começa FORA do
// cruzamento e varre o canteiro (sai da rua). Logo R_direita ≤ meia-via − laneBase.
const TURN_R_RIGHT = 2.0;   // raio da curva à DIREITA (fechada, quadra de dentro)
const TURN_R_LEFT = 7.0;    // raio da curva à ESQUERDA (aberta, varre o cruzamento)
// MEIA-VOLTA: raio IGUAL à faixa. Com R = laneBase o centro do arco cai exatamente no
// EIXO da via, então o carro nunca se afasta mais que `laneBase` do eixo (folga de sobra
// na meia-via de 5) e ATERRISSA na faixa oposta, já alinhado. Sem isto a inversão não era
// reconhecida como curva — reto e meia-volta têm o mesmo produto VETORIAL (zero, direções
// paralelas) — e os 180° sobravam para o esterço de correção de faixa, que é proporcional
// à distância andada: virava um giro lento no meio da pista, com o carro atravessado.
const TURN_R_UTURN = 2.7;   // = laneBase (ver _transform: laneBase do carro é 2.7)
const UTURN_DOT = -0.7;     // produto ESCALAR abaixo disto = inversão (>135°), não curva
const LANE_CTRL_R = 3.2;    // raio do controle de faixa (reto / correção leve)
const TURN_LAT_ACC = 3.6;   // aceleração lateral confortável → velocidade natural (v=√(a_lat·R))
const MAX_YAW = 3.6;        // teto de esterço (rad/s)
/**
 * Rumo TANGENTE ao círculo do arco, na posição angular atual.
 *
 * Referencial deste motor: rumo h → direção (sin h, cos h), e a normal "à direita" é
 * (−seg.z, seg.x) — que para seg=(0,1) aponta para −x. Com o raio u = (cos φ, sin φ):
 *   curva à DIREITA  → tangente = rot+90°(u) = (−sin φ,  cos φ)
 *   curva à ESQUERDA → tangente = rot−90°(u) = ( sin φ, −cos φ)   (idem meia-volta)
 * Conferido numericamente nas 4 direções de marcha e nos 3 tipos de curva.
 */
function arcTangentHeading(arc) {
  const s = Math.sin(arc.phi), c = Math.cos(arc.phi);
  return arc.left ? Math.atan2(s, -c) : Math.atan2(-s, c);
}

// UNIDADES (não radianos): distância máxima entre o carro e o ponto de tangência para a
// curva PODER ser armada. O círculo é tangente à faixa que chega EXATAMENTE em `te`; longe
// dali ele não é o círculo daquela faixa, e percorrê-lo levaria o carro para o lugar errado.
// Dimensionada como um passo do arco (ds ≈ 0,6u na velocidade de curva) mais o clamp lateral
// da faixa (1,2u). Ver `podeCurvar` — é lá que esta folga decide se a curva existe.
const ARC_SNAP = 2.0;

/** Hash determinístico (FNV-1a) → [0,1). Espalha os pontos nominais no segmento do
 *  portal por jogador+prédio+face+geração — SEM Math.random (estável por rota). */
function portalHash(id, bi, face, gen) {
  let h = 2166136261 >>> 0;
  const str = String(id);
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  h ^= (bi + 0x9e3779b9) | 0; h = Math.imul(h, 16777619);
  h ^= (face + 0x85ebca6b) | 0; h = Math.imul(h, 16777619);
  h ^= (gen + 0xc2b2ae35) | 0; h = Math.imul(h, 16777619);
  return (h >>> 0) / 4294967296;
}

export class CityAgents {
  /**
   * @param roadGraph grafo das RUAS (carros)
   * @param walkGraph grafo das CALÇADAS (pedestres)
   */
  constructor(roadGraph, walkGraph) {
    this.roadGraph = roadGraph;
    this.walkGraph = walkGraph;
    this._byId = new Map();
    this._eventCb = null;
    // MULTIPLICADORES de velocidade, vindos do /vendor pelo servidor (welcome + broadcast
    // `sim`). SEPARADOS por tipo: carro e pessoa têm escalas de tempo muito diferentes
    // (base 10–15 contra 2,8–3,8), e acelerar o trânsito sem acelerar quem anda a pé — ou o
    // contrário — é justamente o ajuste que o operador quer no telão.
    //
    // Aplicados onde a velocidade é CONSUMIDA, não no `baseSpeed` de cada avatar: o base é
    // sorteado por avatar e é essa variação que faz o trânsito parecer trânsito. Escalar o
    // campo apagaria o sorteio a cada troca e deixaria a escala dependente da ORDEM dos
    // ajustes (1,5× depois 0,5× ≠ 0,75×). {1,1} = exatamente o comportamento de sempre.
    this.speedScale = { car: 1, person: 1 };
    // COLISÃO entre carros LIGÁVEL POR CONTEXTO (config do admin, ver Settings). Desligar
    // um contexto = os carros NAQUELE contexto ignoram outros carros (passam por cima) —
    // reduz engarrafamento/travas com muitos jogadores. Pedestres NUNCA são atropelados.
    // Padrões: cruzamentos OFF (não trava a interseção), curvas ON (não sobrepõe virando),
    // balizas/meia-volta OFF (não prende), retas ON (fila normal).
    this.collision = { intersection: false, curve: true, uturn: false, straight: true };
    // Carrega a config salva JÁ na criação (antes do 1º tick) — sobrevive a um refresh
    // forçado pelo admin. Protegido: no harness (Node) não há localStorage → usa padrões.
    try {
      const s = typeof localStorage !== 'undefined' && localStorage.getItem('wab.collision');
      if (s) this.setCollisionConfig(JSON.parse(s));
    } catch { /* sem storage → padrões */ }
    this._carsGhost = this._computeGhost();
  }

  /** MODO SEM COLISÃO ENTRE CARROS: todos os contextos carro-carro desligados.
   *  Calculado uma vez por configuração e revalidado 1× por tick (barato: 4 leituras
   *  de booleano) — nunca por carro, nunca alocando. Quando ativo: chegada por
   *  PORTAL (sem reserva de vaga, sem CAR_OCC) e o trabalho exclusivamente
   *  carro-carro é pulado (líder entre carros, bloqueio de cruzamento, contexto de
   *  colisão). Pedestres, limites de via, curvas e rotas seguem valendo sempre. */
  _computeGhost() {
    const c = this.collision;
    return !c.intersection && !c.curve && !c.uturn && !c.straight;
  }

  /** Liga/desliga a colisão entre carros por contexto (persistido no localStorage pela UI). */
  setCollisionConfig(cfg) {
    if (cfg) for (const k in this.collision) if (typeof cfg[k] === 'boolean') this.collision[k] = cfg[k];
    this._carsGhost = this._computeGhost();
  }

  /** API de vaga do tipo do avatar. `acquire(bi, x, z)` pega a vaga LIVRE mais PRÓXIMA de
   *  (x,z): o carro encosta no meio-fio mais perto de onde está, em vez de dar a volta na
   *  quadra até a vaga "preferencial" da frente. Empate cai na ordem de preferência
   *  (frente → laterais → fundos), então com pouca gente o visual segue o de sempre. */
  _slotApi(a) {
    return a.type === 'car'
      ? { acquire: acquireCarSlotNear, release: releaseCarSlot }
      : { acquire: acquirePersonSlot, release: releasePersonSlot };
  }

  onEvent(cb) {
    this._eventCb = cb;
  }
  _emit(type, a) {
    this._eventCb?.(type, a);
  }

  /* ----------------------------- protocolo ----------------------------- */

  /**
   * Novo jogador entra pela borda de uma das 7 vias (laterais + cima/baixo).
   * Escolhe entre as vias MENOS congestionadas com desempate ALEATÓRIO — senão o
   * desempate pegaria sempre a 1ª da lista (as laterais) e a maioria entraria só
   * por elas. Se a "boca" da via sorteada ainda estiver ocupada, devolve false
   * (o backend tenta de novo depois) — a entrada flui sem empilhar avatares.
   */
  spawn(spec) {
    if (this._byId.has(spec.id)) return false; // já existe → não duplica nem vaza slot
    const isCar = spec.type === 'car';
    const graph = isCar ? this.roadGraph : this.walkGraph;
    const entries = graph.entries;
    let best = [];
    let bestNear = Infinity;
    for (const cand of entries) {
      const near = this._countNear(cand.spawn.x, cand.spawn.z, 11, spec.type);
      if (near < bestNear) { bestNear = near; best = [cand]; }
      else if (near === bestNear) best.push(cand);
    }
    const e = best[Math.floor(Math.random() * best.length)];
    if (this._countNear(e.spawn.x, e.spawn.z, 6.5, spec.type) > 0) return false; // boca ocupada
    const node = graph.node(e.nodeId);
    const a = {
      id: spec.id,
      name: spec.name,
      type: 'car', // TODO jogador entra SEMPRE de carro (vira pessoa ao estacionar)
      color: spec.color,
      // o jogador tem os DOIS avatares; troca conforme o deslocamento
      carAppearance: spec.carAppearance,
      personAppearance: spec.personAppearance,
      appearance: spec.carAppearance, // aparência ATUAL (entra de carro)
      graph: this.roadGraph,
      baseSpeed: rnd(10, 15),
      speed: 0,
      radius: 2.6,
      latClamp: 1.2,
      laneBase: 2.7,
      state: 'entering',
      x: e.spawn.x,
      z: e.spawn.z,
      heading: e.spawn.heading,
      lat: 0,
      segDir: null, // direção do segmento atual (p/ a faixa à direita) — carros
      _segFor: null,
      queue: [{ x: node.x, z: node.z }],
      curNode: e.nodeId,
      prevNode: null,
      building: null,
      slot: null,
      slotKind: null, // 'car' | 'person' — qual vaga liberar ao sair
      _then: null,    // ação pendente ao terminar uma caminhada curta (rua/calçada)
      actionData: null,
      _actionAge: 0,   // tempo na ação atual (p/ emitir 'done' ao fim)
      _actionDur: null,
      maneuver: null, // baliza em andamento (meia-volta sem subir na calçada)
      _wait: 0,       // ticks parado (prioridade dinâmica → evita travar p/ sempre)
      directed: !!spec.directed, // backend dirige (não auto-escolhe destino ao entrar)
      _deferred: null, // comando (leave/goto) que chegou durante a transformação
      _dead: false
    };
    this._byId.set(a.id, a);
    return true;
  }

  /**
   * Reconstrói um jogador JÁ ESTACIONADO direto na vaga do prédio `bi`, sem
   * reentrar pela borda — usado pelo snapshot quando um viewer conecta tarde
   * (o servidor não tem x,z; aqui posicionamos parado no destino). NÃO emite
   * eventos (é reconstrução local, não uma chegada real).
   */
  placeAt(spec, bi) {
    if (bi == null || bi < 0 || bi >= BUILDING_COUNT) return false;
    if (this._byId.has(spec.id)) return false; // já existe → não duplica/vaza vaga
    // a coroa contínua nunca fica sem posição → o fallback antigo ({b.x, b.z+8}, que
    // caía DENTRO da pegada nos 13 prédios) deixou de existir
    const slot = acquirePersonSlot(bi);
    const a = {
      id: spec.id,
      name: spec.name,
      type: 'person',
      color: spec.color,
      carAppearance: spec.carAppearance,
      personAppearance: spec.personAppearance,
      appearance: spec.personAppearance, // aparência ATUAL (estacionado = pessoa)
      graph: this.walkGraph,
      baseSpeed: rnd(2.8, 3.8),
      speed: 0,
      radius: 1.4,
      latClamp: 1.0,
      laneBase: 0.9,
      state: 'parked',
      x: slot.x,
      z: slot.z,
      heading: slot.heading,
      lat: 0,
      segDir: null,
      _segFor: null,
      queue: [],
      curNode: this.walkGraph.nearestNode(slot.x, slot.z),
      prevNode: null,
      building: bi,
      slot,
      slotKind: 'person',
      _then: null,
      actionData: null,
      _actionAge: 0,
      _actionDur: null,
      maneuver: null,
      _wait: 0,
      directed: !!spec.directed,
      _deferred: null,
      _dead: false
    };
    this._byId.set(a.id, a);
    return true;
  }

  /**
   * Reconstrói um jogador VAGANDO direto DENTRO da cidade (num nó de rua qualquer,
   * como carro), sem entrar pela borda — usado pelo snapshot quando um viewer
   * conecta tarde e o jogador já estava vagando. Não emite eventos.
   */
  placeWandering(spec) {
    if (this._byId.has(spec.id)) return false;
    const g = this.roadGraph;
    if (!g.nodes || !g.nodes.length) return false;
    const node = g.nodes[Math.floor(Math.random() * g.nodes.length)];
    const a = {
      id: spec.id,
      name: spec.name,
      type: 'car',
      color: spec.color,
      carAppearance: spec.carAppearance,
      personAppearance: spec.personAppearance,
      appearance: spec.carAppearance,
      graph: g,
      baseSpeed: rnd(10, 15),
      speed: 0,
      radius: 2.6,
      latClamp: 1.2,
      laneBase: 2.7,
      state: 'wandering',
      x: node.x,
      z: node.z,
      heading: 0,
      lat: 0,
      segDir: null,
      _segFor: null,
      queue: [],
      curNode: node.id,
      prevNode: null,
      building: null,
      slot: null,
      slotKind: null,
      _then: null,
      actionData: null,
      _actionAge: 0,
      _actionDur: null,
      maneuver: null,
      _wait: 0,
      directed: !!spec.directed,
      _deferred: null,
      _dead: false
    };
    this._byId.set(a.id, a);
    return true;
  }

  /** Existe um avatar com este id? (usado p/ evitar duplicar no snapshot/spawn) */
  has(id) {
    return this._byId.has(id);
  }

  /** Troca o visual do avatar (carro e/ou pessoa). Mantém tipo/posição; o giro de
   *  transformação e o re-render ficam a cargo do EntityManager. */
  restyle(id, appearance) {
    const a = this._byId.get(id);
    if (!a || !appearance) return;
    if (appearance.car) a.carAppearance = appearance.car;
    if (appearance.person) a.personAppearance = appearance.person;
    a.appearance = a.type === 'car' ? a.carAppearance : a.personAppearance;
  }

  _countNear(x, z, r, type) {
    let c = 0;
    const r2 = r * r;
    for (const a of this._byId.values()) {
      if (type && a.type !== type) continue; // carros e pedestres entram em vias distintas
      if ((a.x - x) ** 2 + (a.z - z) ** 2 < r2) c++;
    }
    return c;
  }

  /**
   * Dados da ação a executar ao estacionar. Se vier `durationMs` (e não `loop`),
   * agenda o fim → emite o evento 'done' (telemetria de conclusão).
   *
   * NÃO cancela a celebração pendente — ADIA. O parceiro manda `react` e `achievement`
   * no MESMO lote (visto no /ingress-log: os dois no mesmo segundo, mesmo jogador), e
   * cancelar deixava o avatar PARADO PARA SEMPRE: a conquista existe justamente para
   * devolvê-lo à circulação, e a reação seguinte engolia esse retorno.
   *
   * Com duração, o roam espera a ação terminar — a reação toca inteira e só então o
   * avatar sai de carro. SEM duração (pose indefinida ou `loop`), o relógio da
   * celebração continua valendo: sair a circular é o comportamento que a conquista
   * promete, e uma pose sem fim não pode sequestrá-lo.
   */
  actions(id, data) {
    const a = this._byId.get(id);
    if (!a || a.state !== 'parked') return;
    const dur = data && data.durationMs && !data.loop ? data.durationMs / 1000 : null;
    if (a._celebrate != null && dur != null) a._celebrate = Math.max(a._celebrate, dur);
    a.actionData = data;
    a._actionAge = 0;
    a._actionDur = dur;
  }

  /**
   * Backend manda o jogador ESTACIONADO ir ao próximo edifício. A REGRA decide:
   *   • edifício na MESMA quadra ou VIZINHA → vai a PÉ (vira/segue pessoa);
   *   • edifício em quadra NÃO-vizinha → vira CARRO e vai DIRIGINDO.
   * Ao chegar de carro vira pessoa de novo (estaciona).
   */
  /**
   * Backend manda o jogador `id` ir ao edifício `bi` (índice). `mode`:
   *   • 'auto'  → quadra MESMA/vizinha vai a PÉ; longe vai de CARRO (regra padrão);
   *   • 'walk'  → força a pé (se já for pessoa; senão dirige e estaciona);
   *   • 'drive' → força de carro.
   * Funciona em qualquer estado redirecionável (parked / wandering / entering / em
   * rota). Ignorado durante leaving/transforming.
   */
  goto(id, bi, mode = 'auto') {
    const a = this._byId.get(id);
    if (!a || bi == null || bi < 0 || bi >= BUILDING_COUNT) return;
    if (a.state === 'leaving' || a._leaving) return; // já saindo (inclui indo à rua/girando) → nada o traz de volta
    // chegou durante o giro de transformação → adia p/ o fim (senão seria perdido)
    if (a.state === 'transforming') { a._deferred = () => this.goto(id, bi, mode); return; }
    a.actionData = null; // destino novo → some a ação do prédio anterior
    a._actionDur = null;
    a._celebrate = null; // move durante a celebração → cancela o roam pendente (o jogador vai ao novo prédio)
    a._plazaArrive = false; a._plazaCar = false; // cancela um trajeto p/ a praça em andamento
    a._strollDistrict = null; // …e encerra o passeio pelo distrito (parque), se houver
    const neighbor = a.building != null && buildingsAreNeighbors(a.building, bi);
    const walk = mode === 'walk' || (mode !== 'drive' && neighbor);

    if (walk && a.type === 'person') {
      // a PÉ (já é pessoa): vai direto à vaga do prédio. A vaga anterior é devolvida
      // dentro de _goToBuilding — inclusive a de um destino ainda EM ROTA (antes só
      // soltava se estivesse `parked`, então trocar de destino a caminho vazava a vaga).
      this._goToBuilding(a, bi, 'person');
      return;
    }
    // de CARRO: pedestre anda até a RUA antes de virar carro (não sobe na calçada);
    // quem já é carro só recalcula a rota a partir de onde está.
    if (a.type === 'person') {
      this._walkToRoadThen(a, () => this._goToBuilding(a, bi, 'car'));
    } else {
      this._goToBuilding(a, bi, 'car');
    }
  }

  /**
   * Backend manda o jogador `id` ir à PRAÇA (destino especial — ver cityLayout.PLAZA).
   * Diferente de um prédio: o avatar não estaciona; ao chegar ENTRA na praça e passa
   * a PASSEAR pelos caminhos internos (estado `plaza`). `mode` segue a regra do goto:
   *   • 'auto'  → quadra vizinha da praça vai a PÉ; longe vai de CARRO;
   *   • 'walk'/'drive' → força.
   * De carro, dirige até o meio-fio da praça, vira pessoa e entra a pé.
   */
  gotoPlaza(id, mode = 'auto') {
    if (!PLAZA) return; // cidade sem praça (Mexican Town) → destino não existe aqui
    const a = this._byId.get(id);
    if (!a || a.state === 'leaving' || a._leaving) return; // já saindo → não sequestra a saída
    if (a.state === 'transforming') { a._deferred = () => this.gotoPlaza(id, mode); return; }
    a.actionData = null; a._actionDur = null; a._celebrate = null;
    a._plazaArrive = false; a._plazaCar = false;
    const neighbor = a.building != null && buildingNeighborsPlaza(a.building);
    const walk = mode === 'walk' || (mode !== 'drive' && neighbor);

    if (walk && a.type === 'person') {
      this._walkIntoPlaza(a); // devolve a vaga anterior (inclusive a de um destino em rota)
      return;
    }
    // de CARRO: pessoa anda até a rua, vira carro e dirige até a praça; carro só roteia
    if (a.type === 'person') {
      this._walkToRoadThen(a, () => this._transform(a, 'car', () => this._carToPlaza(a), this._plazaCurbPoint(a)));
    } else {
      this._carToPlaza(a);
    }
  }

  /** PESSOA entra na praça: roteia (calçada) até um nó do anel da fonte e, ao chegar,
   *  começa a passear (estado `plaza`). Guarda o centro p/ orientar a saída depois. */
  _walkIntoPlaza(a) {
    const g = this.walkGraph;
    const goalNode = g.plazaEntryNode(a.x, a.z);
    this._releaseSlot(a); // devolve a vaga do prédio (a praça não tem vaga) — antes de zerar `building`
    a.building = null; a.slotKind = null;
    a.plazaCenter = g.plazaCenter;
    a.maneuver = null; a._segFor = null; a._then = null;
    a.queue = this._routePointsToNode(a, goalNode);
    if (!a.queue.length) { const n = g.node(goalNode); a.queue = [{ x: n.x, z: n.z }]; }
    a.state = 'walking';
    a._plazaArrive = true; // ao esvaziar a fila → entra no passeio da praça
  }

  /** CARRO dirige até o meio-fio da praça (na rua, colado no meio-fio) e, ao chegar,
   *  vira pessoa ALI (nunca sobe na calçada), aterrissa na calçada e entra a pé pelo
   *  braço. Espelha _carArrived, mas sem vaga (a praça não tem estacionamento). */
  _carToPlaza(a) {
    const curb = this._plazaCurbPoint(a);
    this._releaseSlot(a); // devolve a vaga do prédio (a praça não tem vaga) — antes de zerar `building`
    a.building = null; a.slotKind = null;
    a.maneuver = null; a._segFor = null; a._then = null;
    // chega PARALELO na faixa da praça (pela montante da faixa próxima); o giro encosta
    // no meio-fio e a pessoa entra pelo braço
    const wp = this._parkWaypoint(curb, { x: PLAZA.x, z: PLAZA.z });
    a.queue = this._routePoints(a, wp.x - wp.fx * 18, wp.z - wp.fz * 18);
    a.queue.push(wp);
    a.state = 'traveling';
    a._plazaCar = true; // ao chegar (traveling) → vira pessoa e _walkIntoPlaza
    a._plazaCurb = { x: curb.x, z: curb.z };            // meio-fio da praça (onde a pessoa nasce)
    a._plazaHeading = curb.heading;                     // paralelo ao meio-fio
    if (a.speed < 0.5) this._faceQueue(a); // recém-transformado: alinha à via
  }

  /** Vaga do carro no meio-fio da rua que borda a praça, alinhada ao braço mais próximo
   *  do avatar (o carro para NA RUA, colado no meio-fio; a pessoa nasce aí e entra pelo
   *  braço). Mesmo offset das vagas de prédio (CAR_CURB na rua). */
  _plazaCurbPoint(a) {
    const P = PLAZA;
    const cands = [
      { x: P.x, z: P.land.top - CAR_CURB, heading: Math.PI / 2 },       // rua de cima
      { x: P.x, z: P.land.bottom + CAR_CURB, heading: Math.PI / 2 },    // rua de baixo
      { x: P.land.left - CAR_CURB, z: P.z, heading: 0 },                // rua à esquerda
      { x: P.land.right + CAR_CURB, z: P.z, heading: 0 }                // rua à direita
    ];
    let best = cands[0], bd = Infinity;
    for (const c of cands) { const d = (c.x - a.x) ** 2 + (c.z - a.z) ** 2; if (d < bd) { bd = d; best = c; } }
    return best;
  }

  /** Passo do passeio na praça: caminha entre os nós internos (fica na praça). */
  _stepPlaza(a) {
    const g = this.walkGraph;
    const next = a._strollDistrict != null
      ? g.districtWanderStep(a._strollDistrict, a.curNode, a.prevNode, Math.random)
      : g.plazaWanderStep(a.curNode, a.prevNode, Math.random);
    const nd = g.node(next);
    a.queue.push({ x: nd.x, z: nd.z });
    a.prevNode = a.curNode;
    a.curNode = next;
  }

  /** Chegou à praça: vira "morador" da praça (passeia indefinidamente pelos caminhos
   *  internos até um novo goto/roam/leave). Emite 'plaza' (telemetria de chegada). */
  _enterPlaza(a) {
    a._plazaArrive = false;
    a.state = 'plaza';
    a.speed = 0; a.lat = 0; a._segFor = null;
    a.laneBase = 0.5; // caminhos estreitos: anda mais perto do eixo (não raspa a fonte/bancos)
    a.curNode = this.walkGraph.nearestNode(a.x, a.z);
    a.prevNode = null;
    this._emit('plaza', a);
  }

  /**
   * Reconstrói um jogador JÁ passeando, para o snapshot de um viewer que conecta tarde
   * (o servidor guarda o building, sem x,z). Não emite eventos.
   *
   * `district` (opcional) = passeio pelo DISTRITO de um parque da superquadra; sem ele, é
   * a praça das outras cidades. Sem esse ramo, um viewer que recarregasse veria a pessoa
   * PARADA em frente ao parque — o servidor grava `state: parked` ali (o parque não é
   * `plaza` no catálogo), e a reconstrução caía no `placeAt`.
   */
  placeInPlaza(spec, district = null) {
    if (this._byId.has(spec.id)) return false;
    const g = this.walkGraph;
    const pool = district != null ? [...(g.districtNodes.get(district) || [])] : g.plazaHubIds;
    if (!pool || !pool.length) return false;
    const nodeId = pool[Math.floor(Math.random() * pool.length)];
    const n = g.node(nodeId);
    const a = {
      id: spec.id,
      name: spec.name,
      type: 'person',
      color: spec.color,
      carAppearance: spec.carAppearance,
      personAppearance: spec.personAppearance,
      appearance: spec.personAppearance,
      graph: this.walkGraph,
      baseSpeed: rnd(2.8, 3.8),
      speed: 0,
      radius: 1.4,
      latClamp: 1.0,
      laneBase: 0.5, // caminhos estreitos da praça (não raspa a fonte/bancos)
      state: 'plaza',
      x: n.x,
      z: n.z,
      heading: 0,
      lat: 0,
      segDir: null,
      _segFor: null,
      queue: [],
      curNode: nodeId,
      prevNode: null,
      // no distrito o `building` PERMANECE (é o parque): a telemetria de chegada e o
      // "afastar-se do prédio" dependem dele. Na praça continua null, como antes.
      building: district != null ? (spec.building ?? null) : null,
      _strollDistrict: district,
      plazaCenter: district != null ? null : g.plazaCenter,
      slot: null,
      slotKind: null,
      _then: null,
      actionData: null,
      _actionAge: 0,
      _actionDur: null,
      maneuver: null,
      _wait: 0,
      directed: !!spec.directed,
      _deferred: null,
      _dead: false
    };
    this._byId.set(a.id, a);
    return true;
  }

  /** Sugere e vai a um OUTRO edifício (heurística do backend simulado). */
  gotoNext(id) {
    const a = this._byId.get(id);
    if (!a || a.state !== 'parked') return;
    const bi = this._pickOtherBuilding(a);
    if (bi != null) this.goto(id, bi, 'auto');
  }

  /** Backend parou de enviar / mandou sair: avatar VIRA CARRO (se estiver a pé,
   *  indo antes até a rua) e deixa a cidade por uma via. */
  leave(id) {
    { const la = this._byId.get(id); if (la) la._portal = null; } // portal não vale p/ a saída
    const a = this._byId.get(id);
    if (!a || a.state === 'leaving') return;
    // SAÍDA GARANTIDA a partir de AGORA (do comando leave), inclusive uma pessoa a caminho
    // da rua (antes de virar carro). Marca o avatar como "saindo" e arma o detector de
    // FALTA DE PROGRESSO (rede anti-fantasma): _leaveRef = última posição onde ele avançou;
    // _leaveStuck = tempo sem deslocar. Não reinicia se já em saída.
    if (!a._leaving) { a._leaving = true; a._leaveStuck = 0; a._leaveRef = { x: a.x, z: a.z }; }
    a._celebrate = null; // saindo → cancela a celebração de conquista pendente
    a._plazaArrive = false; a._plazaCar = false; // cancela um trajeto p/ a praça em andamento
    a._strollDistrict = null; // …e encerra o passeio pelo distrito (parque), se houver
    // chegou durante o giro de transformação → adia p/ o fim (senão o jogador
    // ficaria "fantasma": o servidor o removeu, mas o avatar nunca sairia).
    if (a.state === 'transforming') { a._deferred = () => this.leave(id); return; }
    const exitRoute = () => {
      const ex = this._nearestEntry(a);
      a.maneuver = null; a._segFor = null;
      a.queue = this._routePointsToNode(a, ex.nodeId);
      a.queue.push({ x: ex.off.x, z: ex.off.z, lane: false });
      a.state = 'leaving';
      a.building = null;
      if (a.type === 'car' && a.speed < 0.5) this._faceQueue(a); // recém-transformado: alinha à via
    };
    if (a.type === 'person') {
      this._walkToRoadThen(a, () =>
        this._transform(a, 'car', exitRoute, this._nearestRoadExitPoint(a))
      );
    } else {
      this._releaseSlot(a);
      a.building = null;
      exitRoute();
    }
  }

  /** Conquista: o avatar ESTACIONADO comemora (postura de pulo) por `ms` e, ao fim,
   *  SAI do prédio e passa a circular (roam). Timer PRÓPRIO (a._celebrate), desacoplado
   *  do mecanismo _actionDur/'done' — assim uma ação legítima do servidor nunca dispara
   *  o roam, e não fabricamos telemetria 'done' que o servidor não pediu. Só coreografa
   *  quem está PARADO num destino (`parked`) ou PASSEANDO nele (`plaza`) — fora disso é
   *  só banner/confete. Rajada: reinicia os 3s. */
  /**
   * Multiplicadores de velocidade vindos do /vendor: `{ car, person }`. Ver `speedScale`.
   *
   * Cada campo é OPCIONAL e independente — mandar só `car` mexe nos carros e deixa os
   * pedestres como estão, que é o ajuste "carros OU pessoas OU os dois" da tela. Valor
   * fora da faixa é IGNORADO em vez de corrigido: o servidor já valida, e um 0 ou um NaN
   * que escapasse congelaria a cidade inteira sem a tela ter como se defender depois.
   */
  setSpeedScale(k) {
    if (!k || typeof k !== 'object') return;
    for (const campo of ['car', 'person']) {
      if (k[campo] == null) continue;
      const n = Number(k[campo]);
      if (!Number.isFinite(n) || n <= 0) continue;
      this.speedScale[campo] = Math.min(3, Math.max(0.25, n));
    }
  }

  /** Passo do timer da celebração. Devolve true se ela ainda está em curso (quem chama
   *  usa isso para congelar o avatar no lugar). Ao zerar: limpa a postura e chama roam. */
  _tickCelebrar(a, dt) {
    if (a._celebrate == null) return false;
    a._celebrate -= dt;
    if (a._celebrate > 0) return true;
    a._celebrate = null;
    a.actionData = null;
    this.roam(a.id);
    return false;
  }

  celebrateThenRoam(id, ms = 3000) {
    const a = this._byId.get(id);
    // `plaza` entra junto: é o estado de quem PASSEIA — a praça das cidades do funil e o
    // parque dos distritos da Mexican. Exigir `parked` fazia a conquista de quem estava ali
    // não coreografar NADA (nem pulo, nem saída de carro); o avatar seguia andando como se
    // o achievement não tivesse chegado. MEDIDO: no B5 (parque) e na praça de acquisition,
    // 30 s depois da conquista o avatar continuava `person/plaza`, tendo andado 65,5u.
    if (!a || (a.state !== 'parked' && a.state !== 'plaza')) return;
    // FECHA a ação que estava em curso ANTES de tomar o avatar. O protocolo (§6) promete
    // que toda `action` com `durationMs` termina em `done`, e a celebração engolia esse
    // retorno — no fluxo NORMAL do jogo, que é `react` e logo em seguida `achievement`.
    // MEDIDO na jornada completa (12 jogadores × 13 edifícios): 156/156 `done` sem a
    // conquista, 0/156 com ela. O vendor pedia o retorno da reação e nunca recebia.
    // O `_emit` vem ANTES de trocar o `actionData`: é dele que saem `action`/`outcome`.
    if (a._actionDur != null) { a._actionDur = null; this._emit('done', a); }
    a.actionData = { kind: 'celebrate' }; // postura de pulo (vai no snapshot enquanto parked)
    a._actionDur = null;                  // a celebração NÃO usa o caminho que emite 'done'
    a._celebrate = ms / 1000;             // ao zerar → roam()
  }

  /** Tira o avatar do prédio e o põe a CIRCULAR (wandering) indefinidamente, até um novo
   *  goto (move) ou leave (timeout). Espelha leave(), mas termina em wander em vez de sair
   *  da cidade. Pessoa: solta a vaga, anda até a rua, vira carro, então vaga. */
  roam(id) {
    const a = this._byId.get(id);
    if (!a || a.state === 'leaving' || a._leaving || a.state === 'wandering') return; // já saindo → não sequestra a saída
    if (a.state === 'transforming') { a._deferred = () => this.roam(id); return; }
    a.actionData = null; a._actionDur = null; a._celebrate = null;
    a._plazaArrive = false; a._plazaCar = false; // cancela um trajeto p/ a praça em andamento
    a._strollDistrict = null; // …e encerra o passeio pelo distrito (parque), se houver
    const startWander = () => { this._releaseSlot(a); a.building = null; this._setWandering(a); }; // devolve a vaga e zera building AO começar a vagar
    if (a.type === 'car') { startWander(); return; }
    this._walkToRoadThen(a, () =>
      this._transform(a, 'car', startWander, this._nearestRoadExitPoint(a))
    );
  }

  /** Ponto da saída de carro mais próxima (p/ orientar o carro recém-formado). */
  _nearestRoadExitPoint(a) {
    const g = this.roadGraph;
    let best = null, bd = Infinity;
    for (const e of g.entries) {
      const n = g.node(e.nodeId);
      const d = (n.x - a.x) ** 2 + (n.z - a.z) ** 2;
      if (d < bd) { bd = d; best = { x: n.x, z: n.z }; }
    }
    return best;
  }

  /**
   * A pessoa está SOLTA dentro de uma superquadra? Devolve o distrito, ou null.
   *
   * "Solta" = dentro da superquadra mas FORA da quadra do próprio `building` — passeando
   * pelo parque, ou a caminho de algum lote quando chegou um destino novo. Para essa gente
   * a saída certa é a rua mais próxima de ONDE ELA ESTÁ; quem está colada na própria
   * quadra continua saindo pela face dela (`personPortalExit`, o fluxo já medido).
   *
   * A condição é POSICIONAL, e não pelo `building`, porque o `building` mente nos dois
   * sentidos:
   *   • ele é trocado para o novo destino no INÍCIO do `_goToBuilding`, então quem ainda
   *     está a caminho de um parque já carrega esse parque com o corpo noutra superquadra
   *     — e a saída mirava o perímetro do distrito do DESTINO. Reproduzido: pessoa em
   *     (60,60), distrito 4, com `building` no B5 (distrito 1), andava 118,5u até
   *     (108,−5). É o que se vê com `move`s em sequência, o fluxo normal do parceiro.
   *   • no passeio ele fica fixado no PARQUE de propósito (é o que sustenta a dedup
   *     1×/edifício, o ranking e a ativação do RSVP), e o lote `park` tem UMA face — o
   *     avatar voltava até ela de qualquer canto do distrito.
   *
   * Também NÃO usa `a._strollDistrict`: goto/leave/roam zeram esse campo ANTES de chamar
   * o `_walkToRoadThen` (para o `_stepPlaza` parar de empurrar passos), então ler dali
   * daria sempre null. Sem estado, nada vaza de uma saída para a seguinte.
   *
   * Trocar a face do prédio pela rua mais próxima do perímetro NÃO mexe em quem já
   * funcionava: medido nas 138 posições de coroa dos prédios em distrito, as duas escolhas
   * caem na MESMA rua em 100% dos casos. Por isso o recorte aqui é só "está solta".
   */
  _distritoDaSaida(a) {
    if (a.type !== 'person') return null;
    if (a.building != null) {
      const L = BUILDINGS[a.building] && BUILDINGS[a.building].land;
      // colada na própria quadra → a face dela é a saída certa (fluxo de sempre)
      if (L && a.x >= L.left && a.x <= L.right && a.z >= L.top && a.z <= L.bottom) return null;
    }
    return DISTRICTS.find((d) =>
      a.x >= d.land.left && a.x <= d.land.right && a.z >= d.land.top && a.z <= d.land.bottom) || null;
  }

  /** Pessoa estacionada anda da calçada até um ponto na RUA e, ao chegar, executa
   *  `then` (virar carro / dirigir). Libera a vaga ao sair. Na PRAÇA, sai antes pelo
   *  braço mais próximo até o meio-fio (senão viraria carro no meio da praça/gramado);
   *  no DISTRITO, pela rua mais próxima de onde está (ver _distritoDaSaida). */
  _walkToRoadThen(a, then) {
    this._releaseSlot(a);
    a.maneuver = null; a._segFor = null;
    a.state = 'walking';
    a._then = then;
    a._plazaArrive = false;
    a._exitPortal = null; // só o ramo de saída por portal (abaixo) define — nunca sobra
    // SAÍDA DE QUEM PASSEAVA PELO DISTRITO — pela rua MAIS PRÓXIMA de onde a pessoa
    // está, e não pela face do parque.
    //
    // O passeio (ver _enterStroll) mantém `a.building` apontando para o PARQUE de
    // propósito: é ele que sustenta a dedup 1×/edifício, o progresso do ranking e o
    // mapeamento de ativação do RSVP por prédio. Só que a saída padrão (abaixo) sai pelo
    // portal de `a.building`, e o lote `park` tem UMA face — então o avatar podia ter
    // andado a superquadra 2×3 inteira e voltava até o parque para virar carro. MEDIDO
    // nos 4 distritos da Mexican: o ponto de saída caía sempre a 19,7–19,8u do centro do
    // parque, qualquer que fosse a posição, e num dos casos a pessoa caminhava 97,5u de
    // volta para sair por ali.
    //
    // Aqui a saída é puramente GEOMÉTRICA, como já era na praça: o meio-fio do perímetro
    // do distrito mais perto da posição ATUAL, alcançado pela calçada — a rota do grafo
    // nunca corta canteiro nem pegada de prédio, ao contrário de uma reta. `_exitPortal`
    // segue null (igual ao ramo da praça): quem orienta o carro é o ponto que o chamador
    // passa ao `_transform`.
    const D = this._distritoDaSaida(a);
    if (D) {
      const L = D.land;
      const cands = [
        { x: clamp(a.x, L.left, L.right), z: L.top - CAR_CURB },     // rua de cima
        { x: clamp(a.x, L.left, L.right), z: L.bottom + CAR_CURB },  // rua de baixo
        { x: L.left - CAR_CURB, z: clamp(a.z, L.top, L.bottom) },    // rua à esquerda
        { x: L.right + CAR_CURB, z: clamp(a.z, L.top, L.bottom) }    // rua à direita
      ];
      // A escolha é pelo TRAJETO, não pela reta. A malha da superquadra contorna os
      // prédios do miolo, então o lado geometricamente mais perto pode custar uma volta
      // inteira: medido em (40,40) do distrito 4, a reta elegia um lado que dava 100,5u
      // de caminhada. São 4 rotas, uma vez por saída — não por quadro.
      let melhor = null, bd = Infinity;
      for (const c of cands) {
        const pts = this._routePoints(a, c.x, c.z);
        if (!pts.length) continue;
        let d = 0, px = a.x, pz = a.z;
        for (const p of pts) { d += Math.hypot(p.x - px, p.z - pz); px = p.x; pz = p.z; }
        d += Math.hypot(c.x - px, c.z - pz); // a perna final, do último nó até o meio-fio
        if (d < bd) { bd = d; melhor = { pts, curb: c }; }
      }
      if (melhor) {
        melhor.pts.push({ x: melhor.curb.x, z: melhor.curb.z, lane: false }); // meio-fio, já na rua
        a.queue = melhor.pts;
        return;
      }
      // sem rota pela calçada: cai no fluxo de sempre em vez de mandar em linha reta
    }
    if (a.plazaCenter) {
      const g = this.walkGraph;
      const tipNode = g.plazaExitTipNode(a.x, a.z);
      const tip = g.node(tipNode);
      const pts = this._routePointsToNode(a, tipNode); // caminha até a ponta do braço
      // empurra além da ponta, até o meio-fio da rua que borda a praça
      const dx = tip.x - a.plazaCenter.x, dz = tip.z - a.plazaCenter.z;
      const d = Math.hypot(dx, dz) || 1;
      pts.push({ x: tip.x + (dx / d) * 5.5, z: tip.z + (dz / d) * 5.5, lane: false });
      a.queue = pts;
      a.plazaCenter = null;
    } else if (a.building != null) {
      // SAÍDA PELA FACE EM QUE A PESSOA ESTÁ — só no modo SEM COLISÃO entre carros.
      //
      // O caminho antigo (abaixo) termina em `nearestCarCurb`, que é a VAGA DE CARRO livre
      // mais próxima. Vaga é recurso do ESTACIONAMENTO, não geometria: com o prédio cheio,
      // a livre mais perto costuma estar em OUTRA FACE, e a pessoa contornava a quadra pela
      // calçada até a esquina antes de virar carro. Sem colisão entre carros não há reserva
      // — qualquer ponto do meio-fio serve —, então a saída pode ser puramente geométrica:
      // a face onde ela já está. Ver Parking.personPortalExit.
      const saida = this._carsGhost ? personPortalExit(a.building, a.x, a.z, a.id) : null;
      if (saida) {
        a._exitPortal = saida; // orienta o carro na transformação (ver _transform)
        // PORTÃO DE ORIGEM: toda saída começa pela projeção PERPENDICULAR da posição na
        // linha de caminhada — a única perna que se pode andar em reta a partir de onde a
        // pessoa está sem raspar canteiro nem pegada. (É o mesmo `gate` que o fluxo
        // antigo, abaixo, já tirava do `personSpotExit`.)
        const from = personSpotGate(a.building, a.x, a.z);
        // DA COROA o portão do portal cai na MESMA linha do portão de origem (as duas
        // funções escolhem a mesma face), e a perna entre eles corre por cima dessa
        // linha — reta e segura. NA SUPERQUADRA não: o portão de origem pode ser uma
        // passarela do miolo e o do portal está sempre no anel do perímetro, a dezenas
        // de unidades — a reta entre eles atravessa canteiro e PRÉDIO (medido: 0,08% dos
        // ticks de pessoa iam por dentro do shopping e do C2). Linhas diferentes ⇒
        // ROTEIA pela calçada, como o fluxo com colisão sempre fez.
        //
        // "Mesma linha" NÃO é "compartilha uma coordenada": a linha do portal é
        // PERPENDICULAR à normal da face (o vetor portão→meio-fio), então é ELA que diz
        // qual coordenada tem de bater. Testar as duas dava falso positivo justamente na
        // superquadra — uma passarela do miolo com o mesmo x do portal, e a "reta" descia
        // por cima dos prédios da coluna inteira.
        const nx = Math.abs(saida.curb.x - saida.gate.x);
        const nz = Math.abs(saida.curb.z - saida.gate.z);
        const mesmaLinha = nx > nz
          ? Math.abs(from.x - saida.gate.x) < 0.5   // normal em x ⇒ linha VERTICAL
          : Math.abs(from.z - saida.gate.z) < 0.5;  // normal em z ⇒ linha HORIZONTAL
        a.queue = [{ x: from.x, z: from.z, lane: false }];
        if (!mesmaLinha) a.queue.push(...this._routePoints(a, saida.gate.x, saida.gate.z, from));
        a.queue.push({ x: saida.gate.x, z: saida.gate.z, lane: false }); // linha de caminhada da face de saída
        a.queue.push({ x: saida.curb.x, z: saida.curb.z, lane: false }); // meio-fio, já na rua
      } else {
        // FLUXO ANTIGO, intacto: colisão LIGADA (a vaga é reserva de verdade) ou prédio sem
        // nenhuma face com rua. Portão da face da pessoa → (portão da face do meio-fio) →
        // vaga de carro, que está SEMPRE na rua. O passo radial fixo de 4,8u que existia
        // aqui só servia para o antigo anel único de vagas: medido, ele já não alcançava o
        // asfalto em 3,6% das posições de hoje (pontas das faces longas do shopping) e
        // falharia em 58% das posições da coroa — e aí o carro nascia em cima da calçada.
        const e = personSpotExit(a.building, a.x, a.z); // e.gate === `from` (mesma função)
        a.queue = [{ x: e.gate.x, z: e.gate.z, lane: false }];
        if (e.gate2.x !== e.gate.x || e.gate2.z !== e.gate.z) {
          // roteia A PARTIR DO PORTÃO, não do nó "à frente" do `_routeStart`: a pessoa
          // ENCARA o prédio, então o nó à frente pode estar do outro lado dele
          a.queue.push(...this._routePoints(a, e.gate2.x, e.gate2.z, e.gate), { x: e.gate2.x, z: e.gate2.z, lane: false });
        }
        a.queue.push({ x: e.curb.x, z: e.curb.z, lane: false });
      }
    } else {
      const cp = this._awayFromBuilding(a, 4.8); // sem prédio (praça já tratada acima) → como antes
      a.queue = [{ x: cp.x, z: cp.z, lane: false }];
    }
  }

  /** Sorteia um edifício diferente do atual (entre os mais próximos → mistura
   *  caminhadas curtas e viagens de carro). */
  _pickOtherBuilding(a) {
    let bi = this.suggestBuilding(a.id);
    if (bi === a.building) bi = (bi + 1 + Math.floor(Math.random() * (BUILDING_COUNT - 1))) % BUILDING_COUNT;
    return bi;
  }

  /** Ponto de PARADA na FAIXA (paralelo ao meio-fio) para o carro estacionar sem apontar
   *  o nariz p/ a calçada. Fica na LINHA DE CENTRO da via (ROADS.width/2−CAR_CURB da vaga);
   *  carrega a vaga (meio-fio) e o pouso da pessoa p/ o giro deslizar até lá. `center` =
   *  centro do prédio/praça (define qual lado é a rua). O carro chega PARALELO e só a largura encara
   *  o meio-fio; o nariz (comprido) fica ao longo da via — nunca sobre a calçada. */
  _parkWaypoint(slot, center) {
    let nx = Math.cos(slot.heading), nz = -Math.sin(slot.heading); // perpendicular ao meio-fio
    if ((slot.x - center.x) * nx + (slot.z - center.z) * nz < 0) { nx = -nx; nz = -nz; } // aponta p/ a RUA
    // laneStop na LINHA DE CENTRO da via (a `back` do meio-fio). É um waypoint de
    // SEGUIR-FAIXA (sem lane:false) → o carro chega PARALELO ao meio-fio (nunca mira o
    // nariz p/ a calçada); ao passar o plano do ponto, o giro encosta no meio-fio e a
    // pessoa entra. Guarda a vaga (curb) e o pouso (land) p/ o deslize do giro.
    const back = ROADS.width / 2 - CAR_CURB;
    // SENTIDO da faixa PRÓXIMA na MÃO AMERICANA (meio-fio à DIREITA do motorista):
    // f = (−nz, nx). O carro aproxima por aqui p/ chegar já na faixa do prédio (a que
    // corre com o meio-fio à sua direita), nunca pela oposta.
    return {
      x: slot.x + nx * back, z: slot.z + nz * back, park: true,
      curbX: slot.x, curbZ: slot.z,           // vaga colada no meio-fio (onde a pessoa nasce)
      heading: slot.heading,                   // paralelo ao meio-fio
      nx, nz,                                  // normal p/ a RUA (mede a faixa próxima × oposta)
      fx: -nz, fz: nx                          // sentido da faixa PRÓXIMA (aproxima por montante)
    };
  }

  /**
   * Escolhe o PORTAL de chegada do edifício `bi` pelo CUSTO REAL da rota (Dijkstra do
   * RoadGraph até o ponto de aproximação de cada portal + rabo euclidiano) — nunca
   * pela distância euclidiana direta. O ponto NOMINAL dentro do segmento vem do hash
   * determinístico (jogador+prédio+face+geração): cada jogador ganha um ponto
   * diferente, estável durante a rota; um novo goto gera nova escolha (geração++).
   * Chamado SÓ no goto (nunca por frame). Devolve null se o prédio não tem portal.
   */
  _choosePortal(a, bi) {
    const portals = carPortals(bi);
    if (!portals.length) return null;
    const g = a.graph;
    const start = this._routeStart(a);
    a._arriveGen = ((a._arriveGen | 0) + 1) & 0xffff; // nova chegada = novos pontos
    let best = null, bestCost = Infinity;
    const costs = []; // custo REAL por portal (registro da escolha — 1× por goto)
    for (let k = 0; k < portals.length; k++) {
      const P = portals[k];
      const t = 0.5 + (portalHash(a.id, bi, k, a._arriveGen) - 0.5) * PORTAL_SPAN;
      const nom = portalPoint(P, t);
      const ax = nom.x - P.fx * 18, az = nom.z - P.fz * 18; // aproximação a montante (igual à vaga)
      // NÓ DE ENTRADA: a esquina A MONTANTE na PRÓPRIA rua do portal. O nó
      // euclidiano mais próximo pode cair na rua do LADO OPOSTO do quarteirão
      // (prédio comprido) — custo e rota passariam POR DENTRO do bloco.
      let node = -1, alongIn = Infinity;
      for (const n of g.nodes) {
        const perp = P.axis === 'x' ? n.z - az : n.x - ax;
        if (Math.abs(perp) > ROADS.width / 2) continue; // fora da rua do portal
        // runway medido até o NOMINAL (não até a aproximação recuada): recuo de 18u
        // deixava a aproximação uns fios A JUSANTE da esquina certa e o filtro pulava
        // p/ a esquina um quarteirão atrás. 4u de pista bastam — a zona do segmento
        // aceita a chegada em qualquer ponto da face.
        const alongNom = (nom.x - n.x) * P.fx + (nom.z - n.z) * P.fz;
        if (alongNom < 4) continue; // sem pista até o nominal — entraria na contramão
        if (alongNom < alongIn) { alongIn = alongNom; node = n.id; }
      }
      let cost = 0;
      if (node >= 0) {
        const path = g.route(start, node);
        for (let i = 1; i < path.length; i++) {
          const n0 = g.node(path[i - 1]), n1 = g.node(path[i]);
          cost += Math.hypot(n1.x - n0.x, n1.z - n0.z);
        }
        cost += alongIn; // trecho final AO LONGO da rua do portal, até o nominal
      } else {
        // rua sem nó a montante (beco): cai no nó mais próximo, como antes
        node = g.nearestNode(ax, az);
        const path = g.route(start, node);
        for (let i = 1; i < path.length; i++) {
          const n0 = g.node(path[i - 1]), n1 = g.node(path[i]);
          cost += Math.hypot(n1.x - n0.x, n1.z - n0.z);
        }
        const last = g.node(path[path.length - 1]);
        cost += Math.hypot(ax - last.x, az - last.z);
      }
      costs.push(cost);
      if (cost < bestCost) { bestCost = cost; best = { P, k, t, nomX: nom.x, nomZ: nom.z, cost, node }; }
    }
    if (best) best.costs = costs;
    return best;
  }

  /** Roteia o avatar até a vaga do edifício `bi`, no modo (tipo) pedido. Se for
   *  preciso trocar de avatar (pessoa↔carro), faz a transformação antes. */
  _goToBuilding(a, bi, needType) {
    const go = () => {
      // PORTÃO DE ORIGEM (só pessoa SAINDO da vaga): a posição na coroa/no piso do
      // distrito fica FORA da linha de caminhada, então o 1º passo da rota — uma reta
      // até o nó de partida — sai de viés e raspa o canteiro (com a planta antiga da
      // superquadra, atravessava o prédio). O portão é a projeção PERPENDICULAR na
      // linha, então a perna posição→portão nunca sai do piso; da mesma forma que a
      // saída para a rua já fazia (ver Parking.personSpotExit). Calculado ANTES de
      // `a.building` virar o destino novo — é o prédio de ONDE ela sai que manda.
      const origem = a.type === 'person' && a.state === 'parked' && a.building != null
        ? personSpotGate(a.building, a.x, a.z)
        : null;
      // DEVOLVE a vaga do destino ANTERIOR antes de reservar a nova. Sem isso, trocar de
      // destino EM ROTA (goto→goto) deixava a reserva antiga presa PARA SEMPRE: as 16
      // vagas de carro de cada prédio iam se esgotando ao longo do evento até TODA
      // chegada cair no ramo `carNoSlot` — a pior concentração possível.
      this._releaseSlot(a);
      a._portal = null; // escolha anterior não vale p/ o novo destino
      // MODO SEM COLISÃO ENTRE CARROS: chegada por PORTAL — sem reservar vaga, sem
      // CAR_OCC, sem busca de vaga exata. O carro vai ao portal de MENOR CUSTO DE
      // ROTA e transforma em qualquer ponto da zona do segmento (ver _driveCar).
      if (a.type === 'car' && this._carsGhost) {
        const escolha = this._choosePortal(a, bi);
        if (escolha) {
          a.building = bi;
          a.slot = null;
          a.slotKind = null; // nada a liberar depois (não há reserva)
          a._portal = escolha; // estável até novo goto/leave (métricas + zona)
          a.plazaCenter = null;
          a.maneuver = null; a._segFor = null;
          a._then = null;
          const wp = this._parkWaypoint({ x: escolha.nomX, z: escolha.nomZ, heading: escolha.P.heading }, buildingPoint(bi));
          wp.portal = escolha.P; // a chegada aceita QUALQUER ponto da zona do segmento
          // recuo da APROXIMAÇÃO limitado ao segmento: 18u fixos podiam atravessar a
          // esquina (nominal perto da borda) e o carro cruzava o plano da vaga AINDA
          // no cruzamento — transformando lá. Recua no máximo até ~6u além da borda
          // do segmento (vizinhança da esquina, nunca o miolo do cruzamento).
          const P = escolha.P;
          const nomAlong = P.axis === 'x' ? escolha.nomX : escolha.nomZ;
          const fAlong = P.axis === 'x' ? wp.fx : wp.fz;
          const disponivel = fAlong > 0 ? nomAlong - P.lo : P.hi - nomAlong;
          const recuo = Math.min(18, Math.max(4, disponivel + 6));
          const ax = wp.x - wp.fx * recuo, az = wp.z - wp.fz * recuo;
          a.queue = this._routePointsToNode(a, escolha.node);
          a.queue.push({ x: ax, z: az, dir: { x: wp.fx, z: wp.fz } });
          a.queue.push(wp);
          a.state = 'traveling';
          if (a.speed < 0.5) this._faceQueue(a);
          return;
        }
        // prédio sem portal (sem rua em face alguma — não deveria): segue o fluxo normal
      }
      let slot = this._slotApi(a).acquire(bi, a.x, a.z);
      // CARRO e o prédio está LOTADO de vagas (comum com muita gente): NÃO inventa um
      // ponto dentro da quadra (isso fazia o carro atravessar a calçada/edifício). Ele
      // para na RUA mais próxima do prédio e vira pessoa ali.
      // Só o CARRO pode ficar sem vaga: a coroa contínua sempre tem posição para uma
      // PESSOA (antes ela caía num ponto fixo atrás do prédio — o mesmo para todos, e
      // DENTRO da pegada no shopping).
      const carNoSlot = a.type === 'car' && !slot;
      if (!slot) slot = { index: null, x: a.x, z: a.z, heading: 0 }; // placeholder; a rota real é o ramo carNoSlot
      a.building = bi;
      a.slot = carNoSlot ? null : slot; // sem vaga: sem ponto na quadra (senão o giro deslizaria p/ lá)
      a.slotKind = a.type; // p/ liberar a vaga certa (carro/pessoa) depois
      a.plazaCenter = null; // saiu/ignora a praça → destino agora é um prédio
      a.maneuver = null; a._segFor = null;
      a._then = null;
      // CARRO numa vaga REAL → chega PARALELO na FAIXA (laneStop); o giro encosta no
      // meio-fio e a pessoa entra na calçada. Pessoa → ponto na calçada (anda até lá).
      const parkable = a.type === 'car' && slot.index != null; // vaga real (não o fallback)
      if (parkable) {
        const wp = this._parkWaypoint(slot, buildingPoint(bi));
        // aproxima pela MONTANTE da faixa PRÓXIMA (−f). Um ponto de APROXIMAÇÃO explícito NA
        // FAIXA da vaga, a montante do meio-fio, é ADICIONADO como waypoint (com `dir` =
        // sentido da faixa) ANTES da vaga. Assim o carro ENTRA na faixa certa AO LONGO da
        // frente do prédio e só chega no waypoint `park` já alinhado e colado. Sem esse ponto,
        // chegando por uma via TRANSVERSAL, o `passed` (que usa a direção do segmento — ainda
        // a da transversal) concluía a vaga JÁ NA ESQUINA (o carro cruzava o plano da vaga a
        // ~25u dela) → o avatar "se transformava longe do meio-fio". O ponto é recuado o
        // suficiente (18u) p/ o carro convergir p/ a faixa da vaga antes do meio-fio.
        const ax = wp.x - wp.fx * 18, az = wp.z - wp.fz * 18;
        a.queue = this._routePoints(a, ax, az);
        a.queue.push({ x: ax, z: az, dir: { x: wp.fx, z: wp.fz } }); // entra na faixa da vaga (ao longo da frente)
        a.queue.push(wp);
      } else if (carNoSlot) {
        // sem vaga de carro LIVRE (todas reservadas por carros a caminho): encosta no MEIO-FIO
        // mais próximo do prédio (mesma aproximação da vaga real, com o ponto intermediário na
        // faixa) e vira pessoa ALI — encosta no meio-fio como qualquer outro, não para no
        // cruzamento distante. Sobreposição breve com um carro a caminho da mesma vaga é ok
        // (o giro é rápido). Sem face com rua (não deveria ocorrer) → nó de rua mais próximo.
        const curb = nearestCarCurb(bi, a.x, a.z);
        if (curb) {
          const wp = this._parkWaypoint(curb, buildingPoint(bi));
          const ax = wp.x - wp.fx * 18, az = wp.z - wp.fz * 18;
          a.queue = this._routePoints(a, ax, az);
          a.queue.push({ x: ax, z: az, dir: { x: wp.fx, z: wp.fz } });
          a.queue.push(wp);
        } else {
          const g = a.graph, p = buildingPoint(bi);
          const nd = g.node(g.nearestNode(p.x, p.z));
          a.queue = this._routePoints(a, nd.x, nd.z);
          if (!a.queue.length) a.queue = [{ x: nd.x, z: nd.z }];
        }
      } else {
        // PESSOA: sai da posição pelo PORTÃO DE ORIGEM, roteia pela calçada até o PORTÃO
        // do destino (projeção radial da posição na linha de caminhada da face mais
        // próxima) e só então dá o passo final até a posição. Os dois trechos portão↔
        // posição têm x OU z constante, então nunca cortam a pegada — é o que permite a
        // posição estar em qualquer profundidade da coroa (ou do piso do distrito).
        const g = personSpotGate(bi, slot.x, slot.z);
        a.queue = [];
        if (origem) a.queue.push({ x: origem.x, z: origem.z, lane: false });
        a.queue.push(...this._routePoints(a, g.x, g.z, origem));
        a.queue.push({ x: g.x, z: g.z, lane: false });
        a.queue.push({ x: slot.x, z: slot.z, lane: false });
      }
      a.state = a.type === 'car' ? 'traveling' : 'walking';
      if (a.type === 'car' && a.speed < 0.5) this._faceQueue(a); // recém-transformado: alinha à via
    };
    if (a.type !== needType) {
      this._transform(a, needType, go, needType === 'car' ? buildingPoint(bi) : null);
    } else go();
  }

  /* --- geometria do prédio (p/ as caminhadas curtas calçada↔rua) --- */
  _buildingCenter(a) {
    if (a.building != null) return buildingPoint(a.building);
    if (a.plazaCenter) return a.plazaCenter; // na praça: "afastar do prédio" = afastar da fonte
    return { x: a.x, z: a.z };
  }
  _towardBuilding(a, dist) {
    const c = this._buildingCenter(a);
    const dx = c.x - a.x, dz = c.z - a.z, d = Math.hypot(dx, dz) || 1;
    return { x: a.x + (dx / d) * dist, z: a.z + (dz / d) * dist };
  }
  _awayFromBuilding(a, dist) {
    const c = this._buildingCenter(a);
    const dx = a.x - c.x, dz = a.z - c.z, d = Math.hypot(dx, dz) || 1;
    return { x: a.x + (dx / d) * dist, z: a.z + (dz / d) * dist };
  }
  _releaseSlot(a) {
    if (a.slot && a.building != null && a.slot.index != null) {
      (a.slotKind === 'person' ? releasePersonSlot : releaseCarSlot)(a.building, a.slot.index);
    }
    a.slot = null;
  }

  /** Inicia a TRANSFORMAÇÃO carro↔pessoa: o avatar fica parado um instante (o
   *  cliente faz o efeito giratório) e, no fim, segue em `then`. A troca de tipo
   *  acontece JÁ no início (a animação esconde).
   *  `curb` (só carro→pessoa): o giro DESLIZA a posição faixa(start)→`curb` (encosta no
   *  meio-fio, na rua) enquanto o carro encolhe — o carro nunca aparece sobre a calçada;
   *  a pessoa nasce no meio-fio e ANDA p/ a calçada (não é empurrada). */
  _transform(a, toType, then, goal, curb, land) {
    a.state = 'transforming';
    a.speed = 0;
    a.maneuver = null;
    a.queue = [];
    // `land` (só carro→pessoa, opcional): ponto NA CALÇADA p/ onde a pessoa desliza na 2ª
    // metade do giro → ela TERMINA na calçada, não parada girando na rua.
    a._tf = { age: 0, then, toType, curb: curb || null, land: land || null, start: { x: a.x, z: a.z } };
    a.type = toType;
    a.appearance = toType === 'car' ? a.carAppearance : a.personAppearance;
    a.graph = toType === 'car' ? this.roadGraph : this.walkGraph;
    a.baseSpeed = toType === 'car' ? rnd(10, 15) : rnd(2.8, 3.8);
    a.radius = toType === 'car' ? 2.6 : 1.4;
    a.latClamp = toType === 'car' ? 1.2 : 1.0;
    a.laneBase = toType === 'car' ? 2.7 : 0.9;
    a.curNode = a.graph.nearestNode(a.x, a.z);
    a.prevNode = null;
    a._segFor = null;
    a.lat = 0;
    // CARRO recém-formado já aponta no SENTIDO DA VIA (rumo ao destino) — termina
    // o giro alinhado, sem nascer atravessado nem precisar manobrar.
    if (toType === 'car') {
      // Saiu por um portal? A orientação vem da FAIXA daquela face, não de um ponto de
      // saída de rua qualquer (`_nearestRoadExitPoint`, que é a esquina mais próxima e
      // pode estar noutra direção). O portal já sabe onde é a via ali.
      if (a._exitPortal) { this._orientFromPortal(a, a._exitPortal, goal); a._exitPortal = null; }
      else this._orientNewCar(a, goal);
    }
  }

  /** Alinha o carro recém-formado com a FAIXA do portal de saída. O portal dá o EIXO da
   *  faixa (`fx`,`fz`); o SENTIDO é o que aponta para o destino — assim ele termina o giro
   *  pronto para seguir, em vez de nascer de costas e ter de dar meia-volta. */
  _orientFromPortal(a, saida, goal) {
    let fx = saida.fx, fz = saida.fz;
    if (goal) {
      const dx = goal.x - a.x, dz = goal.z - a.z;
      if (dx * fx + dz * fz < 0) { fx = -fx; fz = -fz; }
    }
    a.heading = Math.atan2(fx, fz);
    a._segFor = null;
  }

  /** Vira o carro recém-transformado para o 1º nó do trajeto à frente (sentido da
   *  via na direção do destino) — pré-alinha o giro de transformação. */
  _orientNewCar(a, goal) {
    const g = a.graph;
    let w;
    if (goal) {
      const path = this._dropPassedNodes(a, g.route(a.curNode, g.nearestNode(goal.x, goal.z)));
      w = g.node(path[0]);
    } else {
      w = g.node(a.curNode);
    }
    const dx = w.x - a.x, dz = w.z - a.z;
    if (Math.hypot(dx, dz) > 0.5) a.heading = Math.atan2(dx, dz);
  }

  /**
   * CARRO PLANTADO: quer andar, e não sai do lugar.
   *
   * O quebra-deadlock do trânsito vigia a VELOCIDADE (`_wait` só cresce quando `v` cai), e
   * existe um jeito de travar SEM perder velocidade: o carro fica apontado para fora da
   * área dirigível, o passo o levaria além do limite da grade e o `clampCarToRoad` o traz
   * de volta ao mesmo ponto, todo quadro. Como o esterço do arco é proporcional à distância
   * REALMENTE andada, ele também não vira — não anda porque não virou, não vira porque não
   * andou. O vigia de velocidade fica cego: a velocidade está ALTA.
   *
   * REPRODUZIDO com a pilha completa (gateway + rede + motor, 60 jogadores, 8 min): um
   * carro 41 s imóvel na borda da grade com `speed=10,45` e `_wait=0`. Foi o bug relatado
   * com print — o avatar plantado atravessado na rua; num telão de 8 h, o evento inteiro.
   *
   * A saída é vigiar DESLOCAMENTO (como o `_leaveStuck` já faz para quem está saindo) e
   * ATRIBUIR o rumo do próximo waypoint: `heading` atribuído quebra o ciclo, porque não
   * depende de ter andado. Se nem assim sair, volta para o nó de via mais próximo —
   * preservando o destino, ao contrário de re-rotear do zero.
   */
  _vigiarCarroTravado(a, dt) {
    if (a.speed < CAR_STUCK_V) { a._travado = 0; return; } // freando no trânsito: é o outro vigia
    const ref = a._travRef || (a._travRef = { x: a.x, z: a.z });
    if ((a.x - ref.x) ** 2 + (a.z - ref.z) ** 2 >= CAR_STUCK_EPS * CAR_STUCK_EPS) {
      ref.x = a.x; ref.z = a.z; a._travado = 0; a._travCount = 0;
      return;
    }
    if ((a._travado = (a._travado || 0) + dt) < CAR_STUCK_S) return;
    a._travado = 0; ref.x = a.x; ref.z = a.z;
    a.maneuver = null; a._segFor = null;  // abandona a curva em curso — é ela que insiste

    // RE-ROTEIA PELO GRAFO DE RUAS, mantendo o destino. Só mirar o 1º waypoint da fila
    // (`_faceQueue`) não basta e pode ser NOCIVO: numa cidade com SUPERQUADRA esse
    // waypoint pode estar do outro lado do quarteirão, e o carro passa a empurrar contra
    // a calçada do distrito, que o `clampCarToRoad` recusa — o carro fica "tentando
    // invadir a calçada". A rota do grafo só usa RUA por construção, então o waypoint que
    // sai dela é sempre alcançável dirigindo.
    //
    // NUNCA MEXE NA POSIÇÃO. A versão anterior deste vigia, em último caso, movia o carro
    // para o nó de via mais próximo — e isso apareceu na tela como o carro PISCANDO para
    // frente ou para trás. MEDIDO em 12 min na Mexican: 226 saltos, o maior de 50,4u (um
    // carro em (50,3 · 2,7) reapareceu em (0,0), o centro do mapa). Travado é ruim;
    // teleportar na frente do público é pior.
    const fim = a.queue && a.queue.length ? a.queue[a.queue.length - 1] : null;
    if (fim && a.graph) {
      const rota = this._routePoints(a, fim.x, fim.z);
      if (rota.length) a.queue = [...rota, fim];
    }
    this._faceQueue(a); // rumo ATRIBUÍDO (não integrado) para o waypoint já legal
  }

  /**
   * PINO NA QUINA da superquadra compactada. As quadras agora encostam nas ruas, então a
   * QUINA de um distrito fica na borda do cruzamento (ex.: (5,5) na cruz central). Um carro
   * querendo virar ali fica na beira da pista sem poder seguir (a faixa acaba) nem virar (a
   * quina), com o CENTRO na rua mas o CORPO (raio) raspando o distrito — e o clamp o traz de
   * volta todo quadro. É um ciclo-limite: o vigia de deslocamento simples não pega porque a
   * oscilação (~0,67u) zera o ref a cada volta. Aqui a detecção é por CAIXA ENVOLVENTE: se o
   * centro fica numa caixa pequena por PIN_S enquanto o corpo raspa o distrito, solta na malha
   * (volta ao nó de via mais próximo — nunca dentro do distrito) e re-mira; se reincidir, dropa
   * o waypoint que puxava para a quina. Bounda o pino a <1s (era ~10–40s).
   */
  _quebraPinDistrito(a, dt) {
    if (a.speed < CAR_STUCK_V) { a._pin = null; a._pinBreaks = 0; return; } // freando/estacionando: não é pino
    // meia-largura RENDERIZADA (ver CAR_BODY_HALF): com o raio de COLISÃO este teste dava
    // positivo para todo carro na faixa das vias centrais, e o pino "quebrava" sem existir
    const overhang = bodyOverlapsDistrict(a.x, a.z, CAR_BODY_HALF); // pega a quina na diagonal também
    const w = a.queue && a.queue[0];
    if (!overhang || !w) { a._pin = null; return; } // fora do distrito: zera o timer, mas MANTÉM _pinBreaks (o snap tira do overhang; escalonar o drop depende disso)
    // DETECÇÃO POR PROGRESSO: enquanto o CORPO raspa o distrito, o carro precisa se APROXIMAR do
    // waypoint (queue[0]) em ~PIN_PROGRESS por período; se ficar PIN_S sem isso, está preso na quina.
    const d = Math.hypot(w.x - a.x, w.z - a.z);
    const p = a._pin;
    if (!p) { a._pin = { ref: d, t: 0 }; return; }
    if (d <= p.ref - PIN_PROGRESS) { p.ref = d; p.t = 0; a._pinBreaks = 0; return; } // progrediu de verdade → zera tudo
    if ((p.t += dt) < PIN_S) return;
    // Preso raspando a quina → limpa a manobra e RE-MIRA; se reincidir, DROPA o waypoint
    // que puxava para a quina. NÃO mexe na posição: quem tira o corpo de cima do distrito
    // é o afastamento contínuo (ver _afastarDaQuina, aplicado todo quadro enquanto raspa).
    a.maneuver = null; a._segFor = null;
    a._pinBreaks = (a._pinBreaks || 0) + 1;
    if (a._pinBreaks >= 2 && a.queue.length > 1) a.queue.shift(); // dropa o waypoint que puxava p/ a quina
    this._faceQueue(a);
    a._travado = 0; a._travCount = 0;
    a._pin = null;
  }

  /**
   * AFASTA O CORPO DA QUINA, continuamente e sem salto.
   *
   * O pino é um problema LATERAL: o CENTRO do carro está na rua (o `clampCarToRoad` cuida
   * disso), mas o CORPO — o raio — invade a quina do distrito. É isso que o usuário vê
   * como "o carro tentando invadir a calçada da superquadra".
   *
   * A versão anterior soltava o pino movendo o carro para o nó de via mais próximo. Isso
   * de fato tirava o corpo de cima do distrito, mas na tela era o carro PISCANDO para
   * frente ou para trás — MEDIDO em 12 min na Mexican: 226 saltos, o maior de 50,4u (um
   * carro em (50,3 · 2,7) reapareceu em (0,0), o centro do mapa).
   *
   * Aqui o mesmo resultado sai de um empurrão de alguns centímetros por quadro na direção
   * do EIXO da via em que o carro está — que é, por construção, a direção contrária ao
   * distrito. É invisível quadro a quadro, resolve em fração de segundo, e o centro nunca
   * sai da pista (a via tem meia-largura de sobra sobre o raio do carro).
   */
  _afastarDaQuina(a, dt) {
    if (!bodyOverlapsDistrict(a.x, a.z, CAR_BODY_HALF)) return;
    // eixo da via mais próxima (mesmo critério do _startManeuver)
    let cx = a.x, dvx = Infinity;
    for (const v of DRIVE_V) { const d = Math.abs(a.x - v.x); if (d < dvx) { dvx = d; cx = v.x; } }
    let cz = a.z, dhz = Infinity;
    for (const h of DRIVE_H) { const d = Math.abs(a.z - h.z); if (d < dhz) { dhz = d; cz = h.z; } }
    const passo = PIN_PUSH * dt;
    // empurra pelo eixo TRANSVERSAL à via (o que de fato tira o corpo da quina): numa via
    // vertical corrige x; numa horizontal, z. Na dúvida (cruzamento) corrige o mais próximo.
    if (dvx <= dhz) a.x += Math.sign(cx - a.x) * Math.min(passo, Math.abs(cx - a.x));
    else a.z += Math.sign(cz - a.z) * Math.min(passo, Math.abs(cz - a.z));
  }

  /** Aponta o carro EXATAMENTE para o 1º waypoint da fila já montada (sentido da
   *  via). Chamado ao começar a dirigir após a transformação → o heading bate com
   *  a rota, então o carro segue reto, sem manobrar nem desalinhar da rua. */
  _faceQueue(a) {
    const w = a.queue && a.queue[0];
    if (!w) return;
    // mira SEMPRE o 1º waypoint (nó na via, alcançável em linha reta) — nunca um
    // nó adiante que poderia estar "atravessado" cruzando uma quadra.
    const dx = w.x - a.x, dz = w.z - a.z;
    if (Math.hypot(dx, dz) > 0.8) { a.heading = Math.atan2(dx, dz); a._segFor = null; }
  }

  /* ------------------------------ consulta ------------------------------ */

  activeCount() {
    let n = 0;
    for (const a of this._byId.values()) if (a.state !== 'leaving') n++;
    return n;
  }

  /**
   * Sugere um edifício de destino para o avatar `id`: sorteia entre os mais
   * próximos (trajetos curtos → cidade flui e os avatares de fato estacionam).
   * O backend real pode enviar qualquer edifício; isto é só a heurística do sim.
   */
  suggestBuilding(id) {
    const a = this._byId.get(id);
    if (!a) return Math.floor(Math.random() * BUILDING_COUNT);
    const hx = Math.sin(a.heading), hz = Math.cos(a.heading);
    const arr = [];
    for (let i = 0; i < BUILDING_COUNT; i++) {
      const p = buildingPoint(i);
      const dx = p.x - a.x, dz = p.z - a.z;
      arr.push({ i, d: dx * dx + dz * dz, ahead: dx * hx + dz * hz > 0 });
    }
    arr.sort((u, v) => u.d - v.d);
    const near = arr.slice(0, Math.min(6, arr.length));
    // prefere um edifício À FRENTE (menos meia-volta → trânsito mais fluido)
    const ahead = near.filter((o) => o.ahead);
    const pool = ahead.length ? ahead : near;
    return pool[Math.floor(Math.random() * pool.length)].i;
  }

  /** Escolhe um avatar para sair (prefere os que estão só vagando). */
  pickLeaveCandidate() {
    let fallback = null;
    for (const a of this._byId.values()) {
      if (a.state === 'leaving' || a.state === 'transforming') continue;
      if (a.state === 'parked') return a.id; // estacionado é o mais limpo p/ sair
      fallback = a.id;
    }
    return fallback;
  }

  /* ------------------------------- update ------------------------------- */

  /** Avança a simulação um tick e devolve os jogadores p/ o snapshot. */
  update(dt) {
    probes.begin(P.agentes);
    try {
      return this._update(dt);
    } finally {
      probes.end(P.agentes);
    }
  }

  _update(dt) {
    this._carsGhost = this._computeGhost(); // 1× por tick (config pode mudar por fora)
    const arr = [...this._byId.values()];
    arr.forEach((a, i) => (a._k = i));
    renderPos(arr); // posição efetiva = eixo + faixa(direita) + desvio

    // 1) movimento: carros têm modelo cinemático (sempre p/ frente + esterço);
    //    pedestres seguem o modelo simples (podem ajustar de lado na calçada).
    const grid = buildGrid(arr);
    for (const a of arr) {
      // SAÍDA GARANTIDA (sem sumir na tela). O avatar mandado embora segue dirigindo p/ a
      // borda e só é removido quando SAI DE FATO (off-screen) — NUNCA apagado no meio da
      // cidade só porque o trânsito o atrasou (ele continua visível, indo embora). Rede de
      // segurança: se travar DE VEZ (sem deslocar por muito tempo — o "fantasma" que o
      // backend já tirou e o quebra-deadlock não conseguiu mover), remove assim mesmo.
      if (a._leaving) {
        if (Math.abs(a.x) >= EXIT_GONE_X || Math.abs(a.z) >= EXIT_GONE_Z) { a._dead = true; continue; }
        const ref = a._leaveRef || (a._leaveRef = { x: a.x, z: a.z });
        if ((a.x - ref.x) ** 2 + (a.z - ref.z) ** 2 >= LEAVE_STUCK_EPS * LEAVE_STUCK_EPS) {
          ref.x = a.x; ref.z = a.z; a._leaveStuck = 0; // andou o suficiente → zera o relógio de travado
        } else if ((a._leaveStuck = (a._leaveStuck || 0) + dt) > LEAVE_STUCK_S) {
          a._dead = true; continue; // congelado tempo demais → remove (nunca vira fantasma)
        }
      }
      if (a.state === 'transforming') { this._tickTransform(a, dt); continue; }
      if (a.state === 'parked') {
        a.speed = 0;
        // conclusão da ação com duração → emite 'done' uma única vez
        if (a._actionDur != null) {
          a._actionAge += dt;
          if (a._actionAge >= a._actionDur) { a._actionDur = null; this._emit('done', a); }
        }
        // celebração de conquista (timer próprio, SEM 'done'): ao fim, sai e circula
        if (this._tickCelebrar(a, dt)) continue;
        continue;
      }
      // PASSEANDO (praça das cidades do funil, parque dos distritos da Mexican) e
      // comemorando: PARA no lugar enquanto pula. Sem isto o timer nunca corria — ele só
      // existia no ramo `parked` — e a conquista de quem estava na praça/no parque não
      // fazia nada: nem pulo, nem saída de carro. Ver celebrateThenRoam.
      if (a.state === 'plaza' && a._celebrate != null) {
        a.speed = 0;
        this._tickCelebrar(a, dt);
        continue;
      }
      if (a.state === 'wandering') {
        // mantém 2 nós à frente → a curva do arco tem lookahead (senão o próximo nó só
        // aparece ao chegar na esquina e a curva não diferencia raio direita/esquerda)
        while (a.queue.length < 2) this._stepWander(a);
      } else if (a.queue.length === 0) {
        if (a.state === 'plaza') this._stepPlaza(a); // passeia pelos caminhos da praça
        else continue;
      }
      if (!a.queue[0]) continue;
      if (a.type === 'car') {
        this._driveCar(a, grid, dt);
        // TRAVA: garante que o carro nunca terminou o passo fora da pista (calçada/quadra).
        //
        // A correção é LIMITADA por quadro. O clamp tem um liga/desliga duro na borda da
        // grade (`GRID_XLIM`/`GRID_ZLIM`: fora dela, via de acesso, ele nem atua), e um
        // carro que cruza essa fronteira recebia a correção inteira de uma vez — MEDIDO:
        // um salto de 5,3u (z 89,1 → 83,9) bem na boca de entrada, que na tela é o carro
        // PISCANDO. Espalhar a mesma correção por alguns quadros dá o mesmo resultado
        // geométrico sem salto; o teto é maior que qualquer passo legal, então o
        // clamp do dia a dia (frações de unidade) continua instantâneo como antes.
        const cp = clampCarToRoad(a.x, a.z);
        if (cp) {
          const dx = cp.x - a.x, dz = cp.z - a.z;
          const d = Math.hypot(dx, dz);
          if (d <= CLAMP_MAX_STEP) { a.x = cp.x; a.z = cp.z; }
          else { a.x += (dx / d) * CLAMP_MAX_STEP; a.z += (dz / d) * CLAMP_MAX_STEP; }
        }
        this._afastarDaQuina(a, dt);     // tira o CORPO de cima da quina, sem salto
        this._quebraPinDistrito(a, dt);  // solta em <1s o carro que raspa a quina do distrito (esquina compactada)
        this._vigiarCarroTravado(a, dt); // ...e que ele não fique PLANTADO por causa dela
      } else this._walkPerson(a, grid, dt);
    }

    // 2) separação lateral apenas entre pedestres (carros não deslizam de lado)
    this._separate(arr);

    // 3) snapshot (+ remove quem saiu do mapa)
    renderPos(arr);
    const players = [];
    for (const a of arr) {
      if (a._dead) {
        this._byId.delete(a.id);
        this._emit('exited', a);
        continue;
      }
      players.push({
        id: a.id,
        name: a.name,
        type: a.type, // muda ao se transformar (carro↔pessoa)
        color: a.color,
        carAppearance: a.carAppearance,
        personAppearance: a.personAppearance,
        x: a._rx,
        z: a._rz,
        heading: a.heading,
        speed: a.speed,
        state: a.state,
        action: a.state === 'parked' ? a.actionData : null // postura/intent/outcome
      });
    }
    return players;
  }

  /* ------------------------------ internos ------------------------------ */

  _onReach(a) {
    if (a.queue.length > 0) return; // ainda há waypoints
    a.maneuver = null;
    switch (a.state) {
      case 'entering':
        // entrou de carro. Dirigido pelo backend? aguarda comando, vagueando pelas
        // ruas até receber um goto. Autônomo (simulado)? escolhe o 1º edifício.
        if (a.directed) this._setWandering(a);
        else this._goToBuilding(a, this._pickOtherBuilding(a), 'car');
        break;
      case 'traveling': // chegou de CARRO ao meio-fio
        if (a._plazaCar) { // praça: para PARALELO na faixa, gira encostando no meio-fio, pessoa entra pelo braço
          a._plazaCar = false;
          if (a._plazaHeading != null) a.heading = a._plazaHeading;
          const curb = a._plazaCurb;
          a._plazaCurb = null; a._plazaHeading = null;
          this._carBecomePerson(a, curb, null, () => this._walkIntoPlaza(a)); // praça: sem deslize p/ calçada; entra pelo braço
        } else this._carArrived(a); // prédio: vira pessoa e anda p/ a vaga na calçada
        break;
      case 'walking':
        // chegou à PRAÇA (com `_plazaArrive`) → passa a passear pelos caminhos;
        // andou até a RUA (com `_then`) → vira carro/dirige; senão chegou à
        // calçada/prédio → estaciona ali mesmo
        if (a._plazaArrive) this._enterPlaza(a);
        else if (a._then) { const f = a._then; a._then = null; f(); }
        else this._parked(a);
        break;
      case 'plaza':
        break; // passeio contínuo: o loop empurra o próximo passo (_stepPlaza)
      case 'leaving':
        a._dead = true;
        break;
    }
  }

  /** Carro chegou: PARA no meio-fio (na rua), vira PESSOA girando ALI (o carro nunca
   *  sobe na calçada). A pessoa nasce no meio-fio e ANDA até a vaga de PESSOA mais
   *  próxima e livre na calçada. */
  _carArrived(a) {
    // sem prédio válido (só em cenários de teste que dirigem uma rota até o fim, sem destino):
    // vira pessoa ali mesmo, sem procurar vaga (evita índice inválido de vaga).
    if (a.building == null || a.building < 0 || a.building >= BUILDING_COUNT) {
      this._carBecomePerson(a, { x: a.x, z: a.z }, null, () => { a.slot = null; a.slotKind = 'person'; this._parked(a); });
      return;
    }
    // POUSO da pessoa = alvo do DESLIZE do giro (faixa→meio-fio). Usa a VAGA do carro só se
    // ele parou COLADO nela (deslize curto, ~3.5u). Se parou LONGE (ex.: prédio LOTADO → o
    // carro "chega" na esquina, ~20-30u da vaga), NÃO desliza até a vaga — isso seria um
    // TELEPORTE/flutuação de ~20u durante o giro (a pessoa "reaparecia" longe, bug relatado
    // com muitos bots). Nesse caso a pessoa NASCE onde o carro está e ANDA (roteada pela
    // calçada) até a vaga. Alinha o heading ao meio-fio só quando de fato colado.
    const nearSlot = a.slot && a.slot.x != null && Math.hypot(a.slot.x - a.x, a.slot.z - a.z) < 5;
    if (nearSlot && a.slot.heading != null) a.heading = a.slot.heading;
    if (a.slot && a.slotKind === 'car') releaseCarSlot(a.building, a.slot.index);
    const curb = nearSlot ? { x: a.slot.x, z: a.slot.z } : this._towardBuilding(a, 1.5);
    // ALVO NA QUADRA (computado AGORA, antes do giro): a coroa contínua sempre devolve uma
    // posição perto de onde o carro encostou, então some tanto o descarte por distância
    // quanto o fallback "em frente ao carro" — eram eles que faziam todo mundo convergir
    // para meia dúzia de coordenadas quando o prédio enchia.
    const ps = acquirePersonSlotNear(a.building, a.x, a.z);
    a.slot = ps; a.slotKind = 'person';
    a.maneuver = null; a._segFor = null;
    // Se a calçada está PERTO do meio-fio (caso normal: carro encostado na faixa do prédio), o
    // GIRO desliza a pessoa até lá → ela TERMINA na calçada, nunca parada girando na rua (bug
    // relatado: avatares parados fora da calçada). É CURTO (~3u), não o "arrasto" longo de antes
    // (aquele era o carro parando a ~30u). Se longe (carro na FAIXA OPOSTA, raro), ela ANDA
    // (rápido) depois — deslizar longe pareceria arrasto/teleporte.
    const nearWalk = Math.hypot(ps.x - curb.x, ps.z - curb.z) <= PERSON_SLIDE_MAX;
    this._carBecomePerson(a, curb, nearWalk ? ps : null, () => {
      if (nearWalk) { this._parked(a); } // o giro já pousou a pessoa na calçada
      else {
        // longe: ANDA até lá pelo PORTÃO (mesma rota do _goToBuilding). Antes era uma
        // reta de um waypoint só — seguro apenas porque o filtro por distância mantinha
        // o alvo na mesma face; sem ele, a reta cortaria o prédio.
        const g = personSpotGate(a.building, ps.x, ps.z);
        a.queue = this._routePoints(a, g.x, g.z);
        a.queue.push({ x: g.x, z: g.z, lane: false });
        a.queue.push({ x: ps.x, z: ps.z, lane: false });
        a.state = 'walking';
      }
      a._then = null;
    });
  }

  /** CARRO→PESSOA "estacionando na rua": o carro gira na FAIXA e, DURANTE o giro (ele
   *  encolhe), desliza faixa→`curb` (encosta no meio-fio, na rua). A pessoa NASCE no
   *  meio-fio (onde o carro parou) e `then` a faz ANDAR para dentro da calçada/braço.
   *  O carro nunca aparece sobre a calçada. */
  _carBecomePerson(a, curb, land, then) {
    this._transform(a, 'person', then, null, curb, land);
  }

  _parked(a) {
    // PARQUE de superquadra: chegar nele é PASSEIO, não parada — o avatar circula pelo
    // distrito, como na praça das outras cidades. A telemetria continua sendo a chegada
    // NAQUELE prédio (o id de grade do parque), então placar e RSVP não mudam.
    const did = a.building != null ? strollDistrictOf(BUILDING_IDS[a.building]) : null;
    if (did != null) { this._enterStroll(a, did); return; }
    a.state = 'parked';
    a.speed = 0;
    a.lat = 0;
    a._segFor = null;
    // encaixa na vaga (distinta) → pedestres estacionados nunca se sobrepõem
    if (a.slot && a.slot.x != null) { a.x = a.slot.x; a.z = a.slot.z; a.heading = a.slot.heading; }
    this._emit('parked', a);
  }

  /**
   * Chegou no parque do distrito `did`: passa a circular pela malha da superquadra até o
   * próximo goto/roam/leave. Reusa o estado 'plaza' (mesma máquina de passeio); o que
   * muda é o CONFINAMENTO, que vem de `_strollDistrict` em vez do plazaNodeSet.
   *
   * A VAGA é devolvida: o avatar não fica ocupando lugar num prédio onde não está
   * parado — senão as vagas do parque vazavam a cada chegada e as seguintes caíam no
   * ramo sem-vaga. `building` PERMANECE: é ele que a telemetria reporta.
   */
  _enterStroll(a, did) {
    this._releaseSlot(a);
    a.slot = null; a.slotKind = null;
    a._strollDistrict = did;
    a.state = 'plaza';
    a.speed = 0; a.lat = 0; a._segFor = null;
    a.laneBase = 0.5; // passarelas estreitas: anda perto do eixo (não raspa o canteiro)
    a.curNode = this.walkGraph.districtEntryNode(did, a.x, a.z);
    a.prevNode = null;
    this._emit('parked', a); // chegada NO PARQUE (o building segue no agente)
  }

  /** Conta o tempo da transformação (avatar parado girando) e segue ao terminar. */
  _tickTransform(a, dt) {
    a.speed = 0;
    a._tf.age += dt;
    // CARRO→PESSOA: durante o giro (o carro, paralelo, ENCOLHE) ele desliza da faixa
    // (start) até ENCOSTAR no meio-fio (curb), SEM subir na calçada. A pessoa NASCE aí
    // (onde o carro parou, quase no meio-fio) e depois ANDA para dentro da calçada — NÃO
    // "se arrasta" com a posição (o giro não empurra a pessoa p/ a calçada). `then`
    // (em _carArrived / praça) põe a caminhada até a vaga/braço.
    if (a._tf.curb) {
      const r = a._tf.age / TRANSFORM_DUR;
      const p1 = smoothstep01(0.08, 0.44, r); // FASE CARRO: faixa(start) → meio-fio (na rua)
      let x = a._tf.start.x + (a._tf.curb.x - a._tf.start.x) * p1;
      let z = a._tf.start.z + (a._tf.curb.z - a._tf.start.z) * p1;
      // FASE PESSOA (só quando `land` está perto): desliza meio-fio → CALÇADA, então o giro
      // TERMINA na calçada (a pessoa não fica parada girando na rua). Curto (~3u, o carro
      // encosta perto) — não é o "arrasto" longo de antes (aquele era o carro parando a ~30u).
      if (a._tf.land) {
        const p2 = Math.max(0, Math.min(1, (r - 0.44) / 0.5)); // LINEAR (velocidade constante,
        x += (a._tf.land.x - a._tf.curb.x) * p2;                // sem pico) 0.44→0.94 do giro —
        z += (a._tf.land.z - a._tf.curb.z) * p2;                // desliza suave, não "arrasta".
      }
      a.x = x; a.z = z;
    }
    if (a._tf.age >= TRANSFORM_DUR) {
      const then = a._tf.then;
      a._tf = null;
      if (then) then();
      // aplica o comando que chegou durante o giro (leave/goto adiado). Se `then`
      // iniciou outra transformação, o deferred re-adia sozinho (volta a 'transforming').
      if (a._deferred) { const d = a._deferred; a._deferred = null; d(); }
    }
  }

  /**
   * Modelo cinemático do CARRO: anda SEMPRE para frente na direção do heading;
   * o heading gira gradualmente (raio de curva ~ constante) em direção ao alvo;
   * desacelera nas curvas; a anticolisão limita só a VELOCIDADE (nunca empurra
   * o carro de lado). Mira a faixa da DIREITA (offset perpendicular ao alvo).
   */
  _driveCar(a, grid, dt) {
    if (a.maneuver) { this._maneuverCar(a, grid, dt); return; }
    const W = a.queue[0];
    // CHEGADA ANTECIPADA à vaga: se o carro já está PERTO da calçada do prédio, NA FAIXA
    // PRÓXIMA (não na oposta) e ALINHADO com a vaga, transforma AQUI — não segue reto nem
    // dá meia-volta p/ parar do lado oposto (o "pulo" através da rua). perp = distância na
    // normal da rua a partir da vaga (+ = lado da rua); along = quanto o carro está ALINHADO
    // à vaga ao longo do meio-fio. Transforma quando está EM FRENTE à vaga (|along|<3.2) e
    // SOBRE a via (perp em [-1, largura]) — inclusive na faixa oposta: numa aproximação que
    // exige conversão à ESQUERDA o carro chega na faixa de lá e antes ele "chegava" ~30u
    // atrás, no cruzamento (via `passed`), transformando LONGE da quadra (a pessoa "flutuava"
    // de longe). Agora transforma colado à frente do prédio; se ficou na faixa oposta, a
    // pessoa só cruza a rua (curto) até a vaga. Preferimos a faixa próxima, mas não à custa
    // de virar longe. `along` pequeno garante que é EM FRENTE à vaga (não na esquina).
    if (W.park) {
      const cdx = a.x - W.curbX, cdz = a.z - W.curbZ;
      const perp = cdx * W.nx + cdz * W.nz;   // + = lado da rua
      const along = cdx * W.fx + cdz * W.fz;   // ao longo do meio-fio
      let chegou = perp > -1 && perp < ROADS.width / 2 - 1.4 && Math.abs(along) < 3.2;
      // ZONA DO PORTAL (modo sem colisão): o nominal é referência — vale transformar
      // até PORTAL_TOL antes/depois dele, desde que DENTRO do segmento útil (que já
      // exclui as esquinas), na banda perpendicular da rua adjacente (mesmo `perp` da
      // vaga — nunca do outro lado do quarteirão nem numa via só geometricamente
      // próxima) e FORA do centro do cruzamento. O waypoint só existe na fila
      // enquanto ESTE prédio é o destino (goto/leave/praça reconstroem a fila), então
      // destino trocado nunca transforma aqui.
      if (!chegou && W.portal) {
        const coord = W.portal.axis === 'x' ? a.x : a.z;
        chegou = Math.abs(along) < PORTAL_TOL &&
          coord > W.portal.lo && coord < W.portal.hi &&
          perp > -1 && perp < ROADS.width / 2 - 1.4 &&
          !onIntersection(a.x, a.z);
      }
      if (chegou) {
        a.queue.shift(); a._segFor = null; a.maneuver = null;
        this._onReach(a); // fila vazia → _carArrived / praça (vira pessoa aqui)
        return;
      }
    }
    // direção do segmento (fixada quando o waypoint vira o atual). Usa a direção
    // VERDADEIRA da via (nó→nó, anexada ao waypoint como `dir` em _toLanePoints) —
    // NÃO o vetor carro→nó, que era enviesado pelo deslocamento na faixa e fazia o
    // carro tombar p/ o lado ao cruzar e abrir demais as curvas. Sem `dir` (1º trecho
    // após spawn/transform): cai no vetor carro→alvo.
    if (a._segFor !== W) {
      a._segFor = W;
      if (W.dir) {
        a.segDir = W.dir;
      } else {
        // Sem `dir` (só o 1º waypoint de uma rota nova: spawn/entrada, ou o nó de
        // partida que o _dropPassedPoints não descartou por estar colado nele). Usa o
        // RUMO ATUAL como direção do trecho: dirigindo, o carro já está alinhado à via
        // (paralelo à faixa) — spawn/transform pré-orientam o rumo (_orientNewCar/
        // _faceQueue) —, então o rumo é a direção VERDADEIRA da via. Evita medir
        // carro→nó a partir de uma posição deslocada na faixa, que enviesava a 1ª
        // esquina (o carro "puxava" p/ o lado antes de virar). Auto-corrige no 1º
        // waypoint seguinte, que já traz a dir nó→nó verdadeira.
        a.segDir = { x: Math.sin(a.heading), z: Math.cos(a.heading) };
      }
      // (Meia-volta NÃO usa mais a "baliza" (manobra de ré): sob congestionamento ela
      // travava bloqueada, deixando o carro atravessado por segundos. Agora a meia-volta é
      // feita pela GUARDA "siga a via" (gira 180° no sentido do eixo da via rumo a W) e a
      // TRAVA de segurança mantém o carro na pista — rápida e nunca presa.)
    }
    const seg = a.segDir;
    const segH = Math.atan2(seg.x, seg.z);

    // FAIXA DA DIREITA (mão americana): a normal à DIREITA do sentido de marcha é
    // (−seg.z, seg.x) (= f × up). O carro trafega deslocado `laneBase` p/ ESSE lado.
    const rX = -seg.z, rZ = seg.x;
    // linha da faixa que CHEGA (nó deslocado p/ a direita) — referência do seguimento.
    const Lx = W.x + rX * a.laneBase;
    const Lz = W.z + rZ * a.laneBase;

    // CURVA à frente? (o próximo trecho vira ~90°). O raio depende do sentido: à direita
    // fecha, à esquerda abre. `te` = ponto de TANGÊNCIA de ENTRADA do arco, recuado
    // `dEntry` na faixa que chega — o carro segue reto até ali e então faz o arco.
    //   direita: dEntry = laneBase + R (encosta na quadra de dentro)
    //   esquerda: dEntry = R − laneBase (começa antes do nó e varre até a faixa oposta)
    let turnR = 0, teX = 0, teZ = 0, uturn = false, turnLeft = false;
    if (W.lane !== false && !W.park) {
      const N = a.queue[1];
      if (N && N.dir && !N.park) {
        const cr = seg.x * N.dir.z - seg.z * N.dir.x; // <0 → vira à esquerda; >0 → à direita
        const dot = seg.x * N.dir.x + seg.z * N.dir.z; // +1 reto · −1 inversão
        if (dot < UTURN_DOT) {
          // MEIA-VOLTA — trajeto PRÓPRIO, no nó (não no meio do quarteirão). O produto
          // vetorial não distingue isto de "seguir reto" (ambos ~0), por isso o escalar.
          uturn = true;
          turnR = TURN_R_UTURN;
          turnLeft = true;   // meia-volta gira pela ESQUERDA (mão americana)
          const dEntry = 0.5;      // começa praticamente no nó: a volta é NO cruzamento
          teX = Lx - seg.x * dEntry;
          teZ = Lz - seg.z * dEntry;
        } else if (Math.abs(cr) > 0.3) { // curva real (~90°)
          const left = cr < 0;
          turnLeft = left;
          turnR = left ? TURN_R_LEFT : TURN_R_RIGHT;
          const dEntry = left ? Math.max(0.5, turnR - a.laneBase) : (a.laneBase + turnR);
          teX = Lx - seg.x * dEntry;
          teZ = Lz - seg.z * dEntry;
        }
      }
    }

    // ---- rumo-alvo ----
    // ARCO PARAMÉTRICO: termina pelo ÂNGULO VARRIDO, não pelo alinhamento do rumo.
    //
    // Antes o arco era uma perseguição de rumo (alvo = direção de saída) que encerrava quando
    // o rumo alinhava. Sem referência de POSIÇÃO, o erro de entrada era amplificado por R e o
    // carro pousava FORA da faixa de saída; o seguidor de faixa então puxava de volta, e esse
    // puxão é o "balão" que se vê depois da curva. Medido: saída da esquerda (R=7) errava
    // 2,0–4,7u, a da direita (R=2) 0,9–1,4u — o erro escalava com R, como a teoria prevê.
    //
    // Agora o arco é percorrido: o ângulo no círculo avança por COMPRIMENTO (dφ = ds/R) e a
    // posição e o rumo SAEM do círculo. Como o círculo é tangente às duas linhas de faixa
    // (montado no ponto de tangência `te`), o fim da varredura cai exatamente sobre a faixa de
    // saída, com o rumo exato — por construção, sem controlador e sem parâmetro a afinar.
    const arcing = a._arc && a._arc.w === W && a._arc.swept < a._arc.total - 1e-9;
    let targetHeading, ctrlR;
    if (arcing) {
      ctrlR = a._arc.R;
      targetHeading = arcTangentHeading(a._arc); // só p/ `dh` (velocidade/contexto); o rumo real vem do círculo
    } else {
      a._arc = null;
      ctrlR = LANE_CTRL_R;
      if (W.lane === false) {
        targetHeading = Math.atan2(W.x - a.x, W.z - a.z);
      } else {
        const crossErr = (a.x - Lx) * rX + (a.z - Lz) * rZ; // + = à direita da faixa
        // esterça de volta p/ a linha da faixa. Sinal `+` casa com r=(−seg.z,seg.x): à
        // direita da faixa (crossErr>0) → vira p/ a esquerda e volta, e vice-versa.
        targetHeading = segH + clamp(0.14 * crossErr, -0.7, 0.7);
      }
    }

    // GUARDA "SIGA A VIA": num trecho reto (FORA de cruzamento e de arco), se o waypoint
    // atual está EM OUTRA via (não na via em que o carro ESTÁ), o carro NÃO pode ir reto
    // até ele — segue o EIXO da via atual no sentido de W, até um cruzamento onde possa
    // virar. Impede cortar a quadra na diagonal / ficar atravessado quando a rota fica
    // desalinhada (ex.: virou carro no meio do trecho; 1º nó de outra via; segDir velho).
    // Rota correta tem W na PRÓPRIA via (mesmo x numa vertical / mesmo z numa horizontal),
    // então isto só atua no caso degenerado.
    if (!arcing && W.lane !== false) {
      let dvx = Infinity, nvx = 0; for (const r of DRIVE_V) { const d = Math.abs(a.x - r.x); if (d < dvx) { dvx = d; nvx = r.x; } }
      let dhz = Infinity, nhz = 0; for (const r of DRIVE_H) { const d = Math.abs(a.z - r.z); if (d < dhz) { dhz = d; nhz = r.z; } }
      const onV = dvx < ROAD_HALF, onH = dhz < ROAD_HALF;
      // no waypoint de VAGA (park), só alinha se ainda está LONGE dele (noutra via) — perto,
      // deixa o assentamento na faixa/meio-fio agir sem forçar o eixo.
      const nearPark = W.park && Math.hypot(W.x - a.x, W.z - a.z) < ROADS.width;
      if (onV !== onH && !nearPark) { // numa via SÓ (nem cruzamento, nem fora do grid) → usa o EIXO da via
        const ax = onV ? 0 : (W.x >= a.x ? 1 : -1); // eixo da via no sentido de W
        const az = onV ? (W.z >= a.z ? 1 : -1) : 0;
        const arX = -az, arZ = ax;                          // direita do eixo
        const cx = onV ? nvx : a.x, cz = onV ? a.z : nhz;   // centro da via na posição do carro
        const aErr = (a.x - (cx + arX * a.laneBase)) * arX + (a.z - (cz + arZ * a.laneBase)) * arZ;
        targetHeading = Math.atan2(ax, az) + clamp(0.14 * aErr, -0.7, 0.7);
      }
    }

    let dh = wrapAngle(targetHeading - a.heading);
    // MEIA-VOLTA: o erro de 180° é AMBÍGUO (±π dá na mesma para o `wrapAngle`), e o lado
    // não é indiferente — virar à direita jogaria o carro na calçada. Força o giro pela
    // ESQUERDA (rumo CRESCENTE, ver a correção de faixa acima), que é o lado da faixa
    // oposta na mão americana. Só enquanto o erro ainda é grande: passada a metade do
    // giro o sinal normal já aponta certo, e insistir aqui faria uma volta a mais.
    if (arcing && a._arc.uturn && dh < -Math.PI / 2) dh += 2 * Math.PI;

    // CONTEXTO DE COLISÃO (config do admin): curva > meia-volta > cruzamento > reta. Se o
    // contexto está desligado, o carro ignora OUTROS CARROS (nunca pedestres). O estado de
    // meia-volta persiste do momento em que o rumo-alvo aponta p/ trás (>~137°) até alinhar.
    if (this._carsGhost) {
      // modo sem colisão entre carros: não há contexto a classificar (o teste de
      // cruzamento/meia-volta só alimentava a colisão carro-carro) — pedestres
      // continuam valendo SEMPRE no _limitByLead
      a._colOn = false;
    } else {
      if (Math.abs(dh) > UTURN_THRESHOLD) a._uturning = true; else if (Math.abs(dh) < 0.35) a._uturning = false;
      // ESTAR NO CRUZAMENTO vence a manobra. A ordem anterior testava `_arc` primeiro, e
      // como praticamente toda curva de 90° acontece DENTRO de um cruzamento, quem virava
      // era classificado como `curve` (ligada) e nunca como `intersection` (desligada) — o
      // padrão `intersection: false`, que existe para "não travar a interseção", quase
      // nunca era alcançado, e quem travava era justamente quem estava virando.
      //
      // MEDIDO na Mexican, 8 min com 120 avatares: 14 paradas de mais de 6 s DENTRO do
      // arco, a pior de 25,2 s, todas no cruzamento central, com 3 a 10 carros a 0,2–0,9u
      // à frente e NENHUM pedestre — travamento carro-com-carro, no lugar onde a regra
      // manda ignorar carro. O avatar fica atravessado no meio do cruzamento até a fila
      // toda andar.
      const colCtx = onIntersection(a.x, a.z) ? 'intersection'
        : a._arc ? 'curve' : a._uturning ? 'uturn' : 'straight';
      a._colOn = this.collision[colCtx] !== false;
    }

    // VELOCIDADE-ALVO. No ARCO: natural p/ o raio (v=√(a_lat·R)) → curva fechada (direita)
    // mais devagar, aberta (esquerda) mais rápida. Na reta: base, com freada ANTECIPADA e
    // progressiva ao se aproximar de uma curva (rampa) → chega no `te` já no ritmo do arco.
    const base = (a.state === 'entering' ? Math.max(a.baseSpeed, 9) : a.baseSpeed) * this.speedScale.car;
    let v;
    if (arcing) {
      // O teto de curva escala JUNTO com o multiplicador. Sem isso, o ajuste do /vendor
      // era engolido pela curva: √(a_lat·R) é fixo, e num trajeto com esquinas o carro
      // passava a maior parte do tempo nesse teto — MEDIDO, 3× entregava 1,41×, e o
      // operador pedia o triplo e via quase nada. Escalar aqui é seguro porque o arco é
      // PARAMÉTRICO: a posição sai do círculo (dφ = ds/R), então velocidade maior percorre
      // o MESMO arco mais rápido, sem abrir a curva nem sair da faixa.
      v = Math.min(base, Math.sqrt(TURN_LAT_ACC * a._arc.R) * this.speedScale.car);
    } else {
      v = base * clamp(1 - Math.abs(dh) / 0.95, 0.42, 1);
      if (turnR > 0) {
        const near = Math.hypot(teX - a.x, teZ - a.z);
        const vTurn = Math.sqrt(TURN_LAT_ACC * turnR) * this.speedScale.car; // idem: a rampa de freada mira o mesmo teto
        if (near < 14) { const t = clamp((near - 2) / 12, 0, 1); v = Math.min(v, vTurn + (base - vTurn) * t); }
      }
    }
    const freeV = v; // velocidade livre (antes da anticolisão) — referência do quebra-deadlock

    // ANTICOLISÃO na direção ATUAL (pré-esterço): o carro "olha" p/ onde o nariz aponta
    // agora. Só REDUZ a velocidade (nunca desloca de lado). Se o contexto (colCtx) está com
    // colisão DESLIGADA, o carro ignora outros carros (mas nunca pedestres) — ver _limitByLead.
    const hx = Math.sin(a.heading), hz = Math.cos(a.heading);
    v = Math.min(v, this._limitByLead(a, grid, hx, hz, v * dt, dt, false) / dt);
    // "não bloquear o cruzamento": não entra na interseção se não há saída logo depois.
    // 100% carro-carro (o scan pula pedestres) → no modo fantasma é trabalho morto.
    if (!this._carsGhost) v = Math.min(v, this._dontBlockBox(a, grid, hx, hz, v * dt) / dt);
    // QUEBRA-DEADLOCK: travado tempo demais → fura o engarrafamento (ignora carros, cede
    // a pedestres).
    if (a._wait >= FORCE_TICKS) {
      const pedOnly = this._limitByLead(a, grid, hx, hz, FORCE_SPEED * dt, dt, true) / dt;
      v = Math.max(v, Math.min(FORCE_SPEED, pedOnly));
    }
    // DESACELERAÇÃO de CHEGADA à faixa de estacionar (laneStop): freia proporcional à
    // distância → assenta paralelo, sem passar do ponto, antes de virar pessoa.
    if (W.park) {
      const dPark = Math.hypot(W.x - a.x, W.z - a.z);
      v = Math.min(v, Math.max(1.4, dPark * 2.0));
    }

    a.speed = v;
    a._wait = v < Math.max(1.5, freeV * 0.45) ? a._wait + 1 : 0;

    // ESTERÇO. No ARCO (curva deliberada): ∝ DISTÂNCIA andada (modelo bicicleta, ω=v/R) →
    // traça o raio e NUNCA "pivota" no lugar (parado no jam não gira, não trava
    // perpendicular). No RETO/alinhamento: esterço FIRME mesmo devagar → um carro que
    // saiu ATRAVESSADO de uma curva (travou numa colisão no meio dela) ou de uma rota
    // torta ENDIREITA rápido, em vez de sair "andando na diagonal" na reta. No reto o dh
    // já é pequeno quando alinhado, então isto só age de fato quando o carro está torto.
    const ds = v * dt;
    if (arcing) {
      // Percorre o círculo. dφ = ds/R é exatamente a taxa de guinada do modelo de bicicleta
      // para o raio R, então o movimento continua fisicamente coerente — e com v=0 (colisão,
      // pedestre) o ângulo simplesmente não avança: o carro para SOBRE o arco.
      const arc = a._arc;
      const passo = Math.min(ds / arc.R, arc.total - arc.swept);
      arc.swept += passo;
      arc.phi += arc.s * passo;
      // O desvio que o carro trazia ao ENTRAR decai linearmente até zero no fim da varredura:
      // na entrada a posição é exatamente a que ele já tinha (nenhum salto), na saída é
      // exatamente a do círculo (sobre a faixa). Medido: o erro de rumo na entrada tem
      // MEDIANA 0,0° — para o carro típico este termo já nasce nulo.
      const k = arc.total > 1e-9 ? 1 - arc.swept / arc.total : 0;
      a.x = arc.cx + Math.cos(arc.phi) * arc.R + arc.ox * k;
      a.z = arc.cz + Math.sin(arc.phi) * arc.R + arc.oz * k;
      a.heading = arcTangentHeading(arc);
      if (arc.swept >= arc.total - 1e-9) a._arc = null; // varredura completa: solta na faixa
    } else {
      const maxTurn = MAX_YAW * dt;
      a.heading += clamp(dh, -maxTurn, maxTurn);
      // avança SEMPRE para frente, na direção JÁ esterçada
      const dirx = Math.sin(a.heading), dirz = Math.cos(a.heading);
      a.x += dirx * ds;
      a.z += dirz * ds;
    }

    // chegou ao ponto de troca? Numa CURVA é o ponto de tangência de ENTRADA (te) — ali
    // começa o arco até a faixa de saída; em reto é o ponto da faixa (L); vaga/saída, o
    // ponto exato (W). `te`/L ficam SOBRE a trajetória → a troca cai na hora certa (não
    // adiantada, como quando se mirava o nó, que fica `laneBase` de lado e "abria" a curva).
    const isLane = W.lane !== false && !W.park;
    const turning = turnR > 0;
    const tx = turning ? teX : (isLane ? Lx : W.x);
    const tz = turning ? teZ : (isLane ? Lz : W.z);
    // `passed` (trocar ao cruzar o plano do ponto) NÃO vale p/ vaga (`park`): a vaga fica
    // ATRAVESSADA na via de frente e, ao chegar por uma via perpendicular, o carro cruzava
    // o plano da vaga a ~30u dela (na esquina) e "chegava" cedo → transformava LONGE da
    // quadra e a pessoa andava/flutuava de longe. Vaga só conclui pela CHEGADA ANTECIPADA
    // (perto do meio-fio, `|along|<3.2`) ou pela tolerância justa (1.5) — ambas EXIGEM estar
    // colado na vaga. Assim o carro percorre a frente do prédio até o meio-fio antes de virar.
    const passed = W.lane !== false && (a.x - tx) * seg.x + (a.z - tz) * seg.z > 0;
    const adx = tx - a.x, adz = tz - a.z;
    // faixa de estacionar (park): tolerância mais justa → assenta paralelo antes do giro.
    // CURVA: troca praticamente NO `te` (via `passed`) — uma tolerância larga adiantaria
    // o arco e o jogaria p/ fora da rua (o arco à direita não tem folga p/ começar cedo).
    const tol = W.park ? 1.5 : turning ? 0.6 : (W.lane === false ? 2.2 : 2.0);
    if (passed || adx * adx + adz * adz < tol * tol) {
      // waypoint de PORTAL: nunca conclui DENTRO do cruzamento (transformaria no
      // centro dele). Segue em frente; a zona do segmento (acima) ou o próximo tick
      // fora do cruzamento concluem — a guarda "siga a via" mantém o carro na pista.
      if (W.park && W.portal && onIntersection(a.x, a.z)) return;
      a.queue.shift();
      a._segFor = null;
      // CURVA: monta o CÍRCULO tangente à faixa que chega (em `te`) e à faixa que sai.
      // Centro a R de `te`, do lado da curva: direita `te + r·R`, esquerda/meia-volta
      // `te − r·R` (r = normal à direita = (−seg.z, seg.x)). Conferido nas 4 direções de
      // marcha × 3 tipos de curva: a saída pousa na faixa com erro 0 e o arco não sai da via.
      // A CURVA só pode ser armada onde a geometria dela existe.
      //
      // O teste `passed` acima é um SEMIPLANO INFINITO: responde "já passei do plano?", nunca
      // "estou perto do ponto?". Bastava o waypoint estar ATRÁS do carro ou em OUTRA via para
      // o plano já nascer cruzado — e a curva armava na hora, onde quer que o carro estivesse.
      // Medido antes desta guarda, nas 4 cidades: 8–19% dos arcos nasciam FORA de qualquer
      // cruzamento dirigível, a até 59,5u do ponto de tangência (meio de quarteirão). O carro
      // não chegava a sair da pista — `clampCarToRoad` e a guarda "siga a via" o seguravam —,
      // mas era essa briga que produzia o arrasto de lado que se via na tela.
      //
      // Duas condições, as duas necessárias: colado no ponto de tangência (o círculo é
      // tangente à faixa que chega EXATAMENTE ali) e dentro de um cruzamento dirigível (é o
      // único lugar onde virar é legítimo). Sem elas não se arma NADA: o waypoint é consumido
      // e a guarda "siga a via" leva o carro pelo eixo da rua atual até um cruzamento onde a
      // curva seja legal — que é exatamente o papel para o qual ela foi escrita.
      // A pergunta é sobre o NÓ da curva, não sobre onde o carro está. Testar a posição do
      // carro reprovava curvas à DIREITA legítimas: a tangência delas fica a `laneBase + R`
      // = 4,7u do nó e o armamento pode ocorrer 0,6u antes — a 5,3u, fora da caixa de meia-via
      // (5) do `onIntersection`. A esquerda entra a 4,3u e escapava, o que mascarava o erro.
      const podeCurvar = turning
        && (a.x - teX) * (a.x - teX) + (a.z - teZ) * (a.z - teZ) <= ARC_SNAP * ARC_SNAP
        && onIntersection(W.x, W.z);
      if (podeCurvar) {
        const sgnC = turnLeft ? -1 : 1;
        const cx = teX + sgnC * rX * turnR, cz = teZ + sgnC * rZ * turnR;
        const N2 = a.queue[0];
        // varredura: 180° na meia-volta; senão o ângulo real entre a via que chega e a que sai
        const total = uturn ? Math.PI
          : Math.acos(clamp(N2 && N2.dir ? seg.x * N2.dir.x + seg.z * N2.dir.z : 0, -1, 1));
        a._arc = {
          R: turnR, w: N2, uturn, left: turnLeft, cx, cz,
          s: turnLeft ? -1 : 1,                       // sentido do avanço angular
          phi: Math.atan2(teZ - cz, teX - cx),        // ângulo do raio em `te`
          total, swept: 0,
          ox: a.x - teX, oz: a.z - teZ                // desvio de entrada (decai no arco)
        };
      }
      if (a.queue.length === 0) this._onReach(a);
    }
  }

  /** Inicia uma baliza (meia-volta) na via atual rumo a `targetHeading`. */
  _startManeuver(a, targetHeading) {
    // eixo/centro da rua onde o carro está (corredor da manobra)
    let cx = 0, dvx = Infinity;
    for (const r of DRIVE_V) { const d = Math.abs(a.x - r.x); if (d < dvx) { dvx = d; cx = r.x; } }
    let cz = 0, dhz = Infinity;
    for (const r of DRIVE_H) { const d = Math.abs(a.z - r.z); if (d < dhz) { dhz = d; cz = r.z; } }
    const onV = dvx <= dhz; // está mais alinhado a uma via vertical?
    const err = wrapAngle(targetHeading - a.heading);
    a.maneuver = {
      target: targetHeading,
      turnSign: err >= 0 ? 1 : -1, // gira sempre p/ o mesmo lado durante a baliza
      dir: 1,                       // começa de frente; alterna p/ ré ao tocar o meio-fio
      axis: onV ? 'x' : 'z',
      center: onV ? cx : cz,
      age: 0
    };
  }

  /**
   * Executa um passo da baliza: gira o heading sempre p/ o mesmo lado enquanto
   * avança DEVAGAR; ao chegar perto do meio-fio inverte frente↔ré (sem subir na
   * calçada); espera se houver carro/pedestre no rumo do movimento.
   */
  _maneuverCar(a, grid, dt) {
    const m = a.maneuver;
    const err = wrapAngle(m.target - a.heading);
    // concluída — ou trava de segurança (não fica preso manobrando p/ sempre)
    if (Math.abs(err) < 0.18 || ++m.age > MANEUVER_MAX_TICKS) {
      a.heading = m.target;
      a.maneuver = null;
      a.speed = 0;
      return;
    }

    const yawStep = Math.min(MANEUVER_YAW * dt, Math.abs(err)) * m.turnSign;
    const nh = a.heading + yawStep;
    const step = MANEUVER_SPEED * dt;
    const dirx = Math.sin(nh) * m.dir, dirz = Math.cos(nh) * m.dir;
    const nx = a.x + dirx * step, nz = a.z + dirz * step;

    // não subir na calçada: inverte frente↔ré ao tocar o meio-fio. Só bloqueia
    // se estiver indo PARA FORA do eixo (deixa entrar quem vem do meio-fio/vaga).
    const curPerp = m.axis === 'x' ? a.x - m.center : a.z - m.center;
    const perp = m.axis === 'x' ? nx - m.center : nz - m.center;
    if (Math.abs(perp) > CORRIDOR_HALF && Math.abs(perp) > Math.abs(curPerp)) {
      m.dir *= -1; a.speed = 0; return; // toca o meio-fio → inverte (ré/frente)
    }
    // não colidir com carros/pedestres no rumo do movimento → espera abrir espaço
    if (this._maneuverBlocked(a, grid, dirx, dirz)) { a.speed = 0; return; }

    a.heading = nh;
    a.x = nx;
    a.z = nz;
    a.speed = MANEUVER_SPEED;
  }

  /**
   * Anti-gridlock: se o carro vai ENTRAR num cruzamento mas há um carro
   * parado/lento logo DEPOIS dele (saída sem espaço), segura antes do cruzamento
   * em vez de "bloquear o quarteirão". Mantém o cruzamento livre p/ o fluxo
   * transversal → congestionamentos se desfazem em vez de travar.
   */
  _dontBlockBox(a, grid, dirx, dirz, ds) {
    const noseX = a.x + dirx * (a.radius + 1.5);
    const noseZ = a.z + dirz * (a.radius + 1.5);
    if (!onIntersection(noseX, noseZ)) return ds; // não está prestes a entrar
    const exX = a.x + dirx * (a.radius + ROADS.width + 2.5); // logo após o cruzamento
    const exZ = a.z + dirz * (a.radius + ROADS.width + 2.5);
    let blocked = false;
    forEachNeighbor(grid, exX, exZ, (b) => {
      if (b === a || b.type !== 'car' || b.speed > 2.5) return;
      const dx = b._rx - exX, dz = b._rz - exZ;
      if (dx * dx + dz * dz < (a.radius + b.radius) * (a.radius + b.radius)) blocked = true;
    });
    // segura na entrada (mas deixa um "creep" lento → nunca congela de vez)
    return blocked ? ds * 0.12 : ds;
  }

  /** Há carro/pedestre logo no rumo (frente ou ré) da manobra? */
  _maneuverBlocked(a, grid, dirx, dirz) {
    let hit = false;
    forEachNeighbor(grid, a._rx, a._rz, (b) => {
      if (b === a || b.state === 'entering') return;
      const rx = b._rx - a._rx, rz = b._rz - a._rz;
      const fwd = rx * dirx + rz * dirz;
      if (fwd <= 0) return;
      const lat = Math.abs(rx * dirz - rz * dirx);
      if (fwd < a.radius + b.radius + 0.8 && lat < a.radius + b.radius + 0.2) hit = true;
    });
    return hit;
  }

  /** Modelo simples do PEDESTRE: caminha em direção ao próximo ponto da calçada. */
  _walkPerson(a, grid, dt) {
    const target = a.queue[0];
    const dx = target.x - a.x, dz = target.z - a.z;
    const dist = Math.hypot(dx, dz) || 1e-6;
    const dirx = dx / dist, dirz = dz / dist;
    a.heading = Math.atan2(dirx, dirz);
    // NA RUA (recém-desembarcado indo à calçada, ou saindo p/ virar carro) o pedestre (a)
    // IGNORA os CARROS — eles já CEDEM ao pedestre na pista — e (b) anda mais RÁPIDO, p/ SAIR
    // da pista logo (atravessar a rua sem ficar parado no meio/cruzamento — bug relatado:
    // avatares parados na rua da esquina). Fora da rua: velocidade e trânsito normais.
    const onRd = onRoad(a.x, a.z);
    const base = (a.state === 'entering' ? Math.max(a.baseSpeed, 9) : (onRd ? a.baseSpeed * 1.9 : a.baseSpeed)) * this.speedScale.person;
    let ds = this._limitByLead(a, grid, dirx, dirz, base * dt, dt, onRd);
    // QUEBRA-DEADLOCK entre PEDESTRES (fila): quem trava numa multidão fura, senão fica
    // parado para sempre — a separação lateral evita empilhar visualmente.
    // Vale em QUALQUER lugar, não só na pista: com a multidão ocupando a coroa inteira, os
    // caminhantes convergem para o mesmo nó de esquina da calçada e formam CICLO de bloqueio
    // (A cede a B, B a C, C a A). Medido: 12 de 200 congelavam ~165s ali, sem nunca chegar.
    a._pwait = ds < base * dt * 0.5 ? (a._pwait || 0) + 1 : 0;
    const forceAt = onRd ? 5 : FORCE_TICKS; // na rua fura RÁPIDO (~0.6s) p/ sair da pista já
    if (a._pwait >= forceAt) ds = Math.max(ds, Math.min(base, FORCE_SPEED) * dt);
    a.speed = ds / dt;
    if (ds >= dist) {
      a.x = target.x;
      a.z = target.z;
      a.queue.shift();
      this._onReach(a);
    } else {
      a.x += dirx * ds;
      a.z += dirz * ds;
    }
  }

  _setWandering(a) {
    a.state = 'wandering';
    // ENTRA no grafo pela via em que o carro ESTÁ: 1º alvo = nó à FRENTE (no rumo atual),
    // mirado DIRETO (waypoint SEM dir nó-a-nó). Se usasse nearestNode + vizinho (como
    // antes), um carro que virou carro NO MEIO de um trecho miraria um nó de OUTRA via e
    // cortaria a quadra na diagonal (ficava atravessado até a próxima esquina).
    const ahead = this._nodeAhead(a);
    a.curNode = ahead;
    a.prevNode = null;
    a._segFor = null;
    const nd = a.graph.node(ahead);
    a.queue = [{ x: nd.x, z: nd.z }]; // sem dir → o carro entra na via em que está, reto
    this._emit('wandering', a);
  }

  _stepWander(a) {
    const next = a.graph.randomStep(a.curNode, a.prevNode, Math.random);
    const nd = a.graph.node(next);
    const cur = a.graph.node(a.curNode);
    const dx = nd.x - cur.x, dz = nd.z - cur.z, d = Math.hypot(dx, dz) || 1;
    a.queue.push({ x: nd.x, z: nd.z, dir: { x: dx / d, z: dz / d } }); // dir p/ a faixa/alinhamento (ver _toLanePoints)
    a.prevNode = a.curNode;
    a.curNode = next;
  }

  /**
   * Nó de partida da rota: o que o carro JÁ está perseguindo (à frente), para ele seguir
   * em frente em vez de dar meia-volta no meio do quarteirão.
   *
   * ATENÇÃO — o contrato acima vale só para `wandering` e `parked`. Um carro EM ROTA parte
   * do nó mais PRÓXIMO, que muitas vezes é o que ele acabou de passar: a rota nova começa
   * mandando voltar, e como o esterço do arco é proporcional à distância andada isso vira
   * um giro lento no meio da rua (o "u-turn no meio do quarteirão" relatado).
   *
   * TENTEI trocar por `_nodeAhead` também aqui e MEDI o resultado: sob redirecionamento a
   * cada 30 s com 250 carros, as meias-voltas caem de 31 para 22 em acquisition — mas
   * DUAS regressões aparecem, porque `_routeStart` também alimenta o custo de rota da
   * escolha de portal e o descarte de waypoints já passados:
   *   • portalHarness: 5 jornadas passam a escolher portal FORA do custo mínimo;
   *   • trafficGlitchHarness: reaparece um salto de posição (5,3u num tick).
   * Por isso NÃO foi trocado. Resolver de verdade exige separar o nó de partida da
   * NAVEGAÇÃO do nó usado no CUSTO — mudança de contrato, não de uma linha.
   */
  _routeStart(a) {
    if (a.state === 'wandering' && a.curNode != null) return a.curNode;
    if (a.state === 'parked') return this._nodeAhead(a); // sai da vaga p/ frente
    return a.graph.nearestNode(a.x, a.z);
  }

  /** Nó mais próximo À FRENTE do carro (na direção do heading). Evita que o
   *  carro saia da vaga dando meia-volta — ele dá a ré/sai no sentido em que está. */
  _nodeAhead(a) {
    const hx = Math.sin(a.heading), hz = Math.cos(a.heading);
    let best = -1, bd = Infinity;
    for (const n of a.graph.nodes) {
      const dx = n.x - a.x, dz = n.z - a.z;
      if (dx * hx + dz * hz <= 0) continue; // atrás → ignora
      const d = dx * dx + dz * dz;
      if (d < bd) { bd = d; best = n.id; }
    }
    return best >= 0 ? best : a.graph.nearestNode(a.x, a.z);
  }

  /** `from` (opcional): ponto de onde a rota REALMENTE parte — o portão de origem de um
   *  pedestre. Sem ele o nó de partida vem do `_routeStart`, que para quem está na vaga
   *  é o nó À FRENTE: a pessoa ENCARA o prédio, então o nó à frente pode estar do outro
   *  lado dele. (O contrato do `_routeStart` para CARROS fica intacto — ver o comentário
   *  lá sobre as regressões de portal/tráfego.) */
  _routePoints(a, gx, gz, from) {
    const g = a.graph;
    const goal = g.nearestNode(gx, gz);
    const start = from ? g.nearestNode(from.x, from.z) : this._routeStart(a);
    return this._dropPassedPoints(a, this._toLanePoints(g, g.route(start, goal)));
  }

  _routePointsToNode(a, nodeId) {
    const g = a.graph;
    return this._dropPassedPoints(a, this._toLanePoints(g, g.route(this._routeStart(a), nodeId)));
  }

  /** Converte um caminho de nós (ids) em waypoints da via, anexando `dir` = direção
   *  UNITÁRIA do trecho que TERMINA naquele nó (nó→nó). É essa direção VERDADEIRA da
   *  via — e não o vetor carro→nó (enviesado porque o carro anda deslocado na faixa) —
   *  que orienta a faixa da direita, o alinhamento e a mitra das esquinas. Sem ela o
   *  carro "tombava" p/ o lado ao cruzar e abria demais as curvas. */
  _toLanePoints(g, path) {
    const pts = [];
    for (let i = 0; i < path.length; i++) {
      const n = g.node(path[i]);
      const pt = { x: n.x, z: n.z };
      if (i > 0) {
        const p = g.node(path[i - 1]);
        const dx = n.x - p.x, dz = n.z - p.z, d = Math.hypot(dx, dz) || 1;
        pt.dir = { x: dx / d, z: dz / d };
      }
      pts.push(pt);
    }
    return pts;
  }

  /** Igual ao _dropPassedNodes, mas sobre waypoints já com `dir` (preserva a direção
   *  verdadeira do trecho ao descartar o nó inicial já ultrapassado). */
  _dropPassedPoints(a, pts) {
    if (a.type !== 'car') return pts;
    while (pts.length >= 2) {
      const n0 = pts[0], n1 = pts[1];
      const dx = n1.x - n0.x, dz = n1.z - n0.z;
      const len2 = dx * dx + dz * dz || 1;
      const t = ((a.x - n0.x) * dx + (a.z - n0.z) * dz) / len2; // 0=em n0, 1=em n1
      const perp = Math.hypot(a.x - (n0.x + t * dx), a.z - (n0.z + t * dz));
      if (t > 0.05 && t < 0.95 && perp < ROAD_HALF + 1.5) pts.shift();
      else break;
    }
    return pts;
  }

  /** CARRO: remove o nó inicial APENAS quando o carro está REALMENTE sobre o trecho
   *  n0→n1 (na via, entre os dois nós) — aí ele segue em frente em vez de voltar à
   *  interseção atrás. Se NÃO estiver claramente sobre o trecho, mantém o nó mais
   *  próximo (seguro): impede mirar um nó "atravessado" e cortar pelos edifícios. */
  _dropPassedNodes(a, path) {
    if (a.type !== 'car') return path;
    const g = a.graph;
    while (path.length >= 2) {
      const n0 = g.node(path[0]), n1 = g.node(path[1]);
      const dx = n1.x - n0.x, dz = n1.z - n0.z;
      const len2 = dx * dx + dz * dz || 1;
      const t = ((a.x - n0.x) * dx + (a.z - n0.z) * dz) / len2; // 0=em n0, 1=em n1
      const perp = Math.hypot(a.x - (n0.x + t * dx), a.z - (n0.z + t * dz));
      // só descarta n0 se o carro está COMPROVADAMENTE sobre o trecho n0→n1 (na
      // via, entre os dois nós) — senão mantém e vai reto ao nó mais próximo.
      if (t > 0.05 && t < 0.95 && perp < ROAD_HALF + 1.5) path.shift();
      else break;
    }
    return path;
  }

  _nearestEntry(a) {
    const g = a.graph;
    let best = g.entries[0];
    let bd = Infinity;
    for (const e of g.entries) {
      const n = g.node(e.nodeId);
      const d = (n.x - a.x) ** 2 + (n.z - a.z) ** 2;
      if (d < bd) { bd = d; best = e; }
    }
    return best;
  }

  /**
   * Limita o avanço `ds` para não colar no agente à frente / ceder a pedestre.
   * Com `ignoreCars`, ignora os outros CARROS (quebra-deadlock) mas continua
   * cedendo a pedestres — o carro liberado fura o engarrafamento sem atropelar.
   */
  _limitByLead(a, grid, dirx, dirz, ds, dt, ignoreCars) {
    let allowed = ds;
    // FREIO SUAVE: cada restrição limita o avanço a `gap × K` (K = dt/HEADWAY) em vez
    // de cravar no gap — velocidade ∝ distância restante → desaceleração progressiva,
    // micro-movimento contínuo (nunca congela) e retomada imediata. Como gap×K < gap,
    // NUNCA avança além do ponto de parada antigo (segurança idêntica).
    const K = Math.min(1, dt / HEADWAY);
    const cap = (gap) => { const lim = Math.max(0, gap) * K; if (lim < allowed) allowed = lim; };
    forEachNeighbor(grid, a._rx, a._rz, (b) => {
      if (b === a) return;
      const rx = b._rx - a._rx;
      const rz = b._rz - a._rz;
      const forward = rx * dirx + rz * dirz;
      if (forward <= 0) return; // atrás
      const lateral = Math.abs(rx * dirz - rz * dirx); // distância perpendicular

      // CARRO CEDE A PEDESTRE que está NA RUA à frente: para bem antes, deixando
      // a faixa de travessia livre (nunca atropela).
      if (a.type === 'car' && b.type === 'person') {
        // só cede a pedestre DENTRO da pista (atravessando) — não a quem está na beira
        // da calçada (senão multidões travam o trânsito) NEM a quem está em GIRO
        // (carro↔pessoa gira parado no meio-fio; não é pedestre atravessando).
        if (b.state === 'parked' || b.state === 'transforming' || !onRoadStrict(b._rx, b._rz)) return;
        if (lateral > a.radius + b.radius + 1.8) return;
        cap(forward - (a.radius + b.radius) - 3.2);
        return;
      }

      // PEDESTRE "olha antes de atravessar": só pausa para carro VELOZ e iminente
      // (prioridade do pedestre — passa na frente de carro já parado/freando). Com
      // `ignoreCars` (pedestre NA RUA indo p/ a calçada) nem isso: cruza direto (o carro
      // cede) p/ sair da pista rápido, sem ficar parado no meio da rua.
      if (a.type === 'person' && b.type === 'car') {
        if (ignoreCars || b.state === 'parked' || b.speed < 4 || forward > 5.5) return;
        if (lateral > a.radius + b.radius + 0.5) return;
        cap(forward - (a.radius + b.radius) - 0.6);
        return;
      }

      if (b.state === 'parked' || a.type !== b.type) return;

      // CARRO x CARRO
      if (a.type === 'car') {
        // modo "liberado" (quebra-deadlock): ignora completamente os outros
        // carros p/ furar o engarrafamento na hora (sobreposição breve só
        // durante a liberação é aceitável e preferível a ficar travado).
        // Também ignora se o CONTEXTO do carro está com colisão DESLIGADA (config):
        // cruzamento/curva/baliza/reta — ver `a._colOn` em _driveCar. Pedestres, acima,
        // seguem valendo sempre (o carro nunca atropela).
        if (ignoreCars || !a._colOn) return;
        const sum = a.radius + b.radius;
        if (Math.cos(a.heading - b.heading) > 0.5) {
          // mesma direção → fila (mantém distância, desacelerando suave)
          if (lateral <= sum * 0.8) cap(forward - sum - FOLLOW_BUFFER);
          return;
        }
        // cruzando/oposto: cede só o de menor prioridade. Desempate dinâmico por
        // tempo de espera (quem espera mais ganha preferência → sem inanição),
        // com folga p/ não oscilar. Sempre cede a quem está manobrando.
        const mustYield = b.maneuver
          ? true
          : a.maneuver
            ? false
            : a._wait < b._wait - 3 || (Math.abs(a._wait - b._wait) <= 3 && a._k > b._k);
        if (mustYield && forward < sum + 2.6 && lateral < sum + 0.2) {
          cap(forward - sum - 1.2);
        }
        // anti-atropelamento de carro: NINGUÉM (nem o de prioridade) entra dentro
        // de outro logo à frente — janela curtíssima, não trava o trânsito.
        if (forward < sum + 0.9 && lateral < sum * 0.7) {
          cap(forward - sum - 0.3);
        }
        return;
      }

      // PEDESTRE x PEDESTRE: fila na mesma direção
      if (Math.cos(a.heading - b.heading) < 0.5) return;
      if (lateral > (a.radius + b.radius) * 0.8) return;
      cap(forward - (a.radius + b.radius) - FOLLOW_BUFFER);
    });
    return allowed;
  }

  /** Empurra lateralmente pedestres sobrepostos (desvio que decai).
   *  Carros NÃO entram aqui — eles nunca deslizam de lado (só freiam). */
  _separate(arr) {
    for (const a of arr) if (a.type !== 'car' && a.state !== 'parked') a.lat *= LAT_DECAY;
    // várias passadas de relaxação → multidões densas de fato se separam
    for (let it = 0; it < 3; it++) {
      renderPos(arr);
      const grid = buildGrid(arr);
      for (const a of arr) {
        if (a.type === 'car') continue; // pula carros
        forEachNeighbor(grid, a._rx, a._rz, (b) => {
          if (b._k <= a._k || b.type === 'car') return;
          if (a.state === 'parked' && b.state === 'parked') return; // ambos imóveis: empurrão é zero (abaixo) — sai antes de calcular

          const dx = b._rx - a._rx;
          const dz = b._rz - a._rz;
          const min = a.radius + b.radius + MARGIN;
          const d2 = dx * dx + dz * dz;
          if (d2 >= min * min || d2 < 1e-9) return;
          const d = Math.sqrt(d2);
          const nx = dx / d, nz = dz / d;
          const overlap = (min - d) * 0.5;
          const aMov = a.state !== 'parked', bMov = b.state !== 'parked';
          const aPush = aMov ? (bMov ? overlap : overlap * 2) : 0;
          const bPush = bMov ? (aMov ? overlap : overlap * 2) : 0;
          // componente perpendicular ao heading vira desvio lateral
          if (aPush) a.lat += -(nx * Math.cos(a.heading) - nz * Math.sin(a.heading)) * aPush;
          if (bPush) b.lat += (nx * Math.cos(b.heading) - nz * Math.sin(b.heading)) * bPush;
        });
      }
      for (const a of arr) if (a.type !== 'car') a.lat = clamp(a.lat, -a.latClamp, a.latClamp);
    }
  }

  _remove(a) {
    this._byId.delete(a.id);
  }
}

/* --------------------------- grade espacial --------------------------- */

/** Posição RENDERIZADA (a._rx/a._rz). Carros já dirigem na faixa certa, então
 *  usam a posição física direta. Pedestres recebem o offset de calçada + desvio. */
function renderPos(arr) {
  for (const a of arr) {
    if (a.type === 'car') { a._rx = a.x; a._rz = a.z; continue; }
    // Parado E transformando renderizam SEM o desvio de faixa: durante o giro o
    // avatar está imóvel e o `type` já é 'person', então aplicar o laneBase empurraria
    // o (ainda visível) modelo de CARRO para o lado da calçada. Renderiza no ponto
    // exato (a.x/a.z) → o carro fica na rua e a pessoa aterrissa na calçada.
    const still = a.state === 'parked' || a.state === 'transforming';
    const off = (still ? 0 : a.laneBase) + a.lat;
    a._rx = a.x + Math.cos(a.heading) * off;
    a._rz = a.z - Math.sin(a.heading) * off;
  }
}

/**
 * Chave de célula INTEIRA. Era `Math.floor(x/CELL) + ',' + Math.floor(z/CELL)`:
 * concatenar duas strings e hashear a string resultante, 500 vezes para montar a
 * grade e mais 9 por agente para consultar — 3 vezes por tick (são 3 passadas de
 * relaxação). Um inteiro empacotado não aloca e o Map usa comparação numérica.
 *
 * Medido de ponta a ponta com 500 agentes, 3 sementes: **tick 3,24 → 2,32 ms
 * (−28,7%)**, alocação por tick 64 → 27 KB. Comportamento BIT A BIT idêntico
 * (hash de x/z/heading/lat/state dos 500 agentes ao longo de 500 ticks).
 *
 * O deslocamento de +512 é obrigatório: os agentes chegam a ±157 nos pontos de
 * entrada e saída (o EXIT_GONE só remove em 150/145), então um array plano de
 * 40×40 estouraria. Com o deslocamento, o índice cabe em 10 bits por eixo.
 */
const GRID_BIAS = 512;
const cellKey = (cx, cz) => (((cx + GRID_BIAS) << 10) | (cz + GRID_BIAS));

function buildGrid(arr) {
  const grid = new Map();
  for (const a of arr) {
    const k = cellKey(Math.floor(a._rx / CELL), Math.floor(a._rz / CELL));
    let b = grid.get(k);
    if (!b) grid.set(k, (b = []));
    b.push(a);
  }
  return grid;
}

function forEachNeighbor(grid, x, z, cb) {
  const cx = Math.floor(x / CELL);
  const cz = Math.floor(z / CELL);
  for (let dx = -1; dx <= 1; dx++) {
    for (let dz = -1; dz <= 1; dz++) {
      const bucket = grid.get(cellKey(cx + dx, cz + dz));
      if (bucket) for (const b of bucket) cb(b);
    }
  }
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/** Smoothstep 0..1 entre e0 e e1 (curva S). */
function smoothstep01(e0, e1, x) {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** Normaliza um ângulo para (-π, π]. Por RESTO — ângulo acumulado por horas de
 *  simulação faria o laço iterar uma vez por volta já dada. */
function wrapAngle(a) {
  const TAU = 2 * Math.PI;
  let r = a % TAU;
  if (r > Math.PI) r -= TAU;
  else if (r < -Math.PI) r += TAU;
  return r;
}

/** Está sobre o asfalto de alguma via? (usado p/ o carro ceder só a quem cruza) */
const ROAD_HALF = ROADS.width / 2;
function onRoad(x, z) {
  for (const r of DRIVE_V) {
    if (Math.abs(x - r.x) < ROAD_HALF && z > r.zMin - ROAD_HALF && z < r.zMax + ROAD_HALF) return true;
  }
  for (const r of DRIVE_H) {
    if (Math.abs(z - r.z) < ROAD_HALF && x > r.xMin - ROAD_HALF && x < r.xMax + ROAD_HALF) return true;
  }
  return false;
}

// TRAVA DE SEGURANÇA: um CARRO nunca pode sair da pista (subir na calçada / atravessar
// a quadra/edifício). `DRIVE_HALF` é a meia-largura DIRIGÍVEL (corpo do carro dentro da
// pista); as curvas normais chegam a ~3.4 da linha de centro, então 3.9 não atrapalha.
// Só atua DENTRO do grid (fora dele são as vias de acesso, que não têm quadras).
const DRIVE_HALF = ROAD_HALF - 1.1; // 3.9
// Limite da GRADE: cobre todas as quadras com folga; além dele estão as vias de acesso
// (que se prolongam mundo afora e não devem ser clampadas). DERIVADO da malha — era fixo
// em 124/78, valores da grade 5×3, e na 4×4 (que vai até ±97) a trava simplesmente
// deixava de atuar na faixa externa da cidade: era ali que o carro cortava a quina e
// pisava no canteiro. A conta reproduz 124/78 exatos na 5×3.
const GRID_XLIM = Math.max(...DRIVE_V.map((r) => r.x)) + ROADS.width / 2 + 4;
const GRID_ZLIM = Math.max(...DRIVE_H.map((r) => r.z)) + ROADS.width / 2 + 4;
/** Se (x,z) de um carro saiu da faixa dirigível, projeta de volta na via mais próxima —
 *  remove só o desvio PERPENDICULAR (mantém o avanço ao longo da via) → nunca cruza a
 *  quadra. Retorna null se não precisa corrigir (já na pista, ou fora do grid). */
function clampCarToRoad(x, z) {
  if (Math.abs(x) > GRID_XLIM || Math.abs(z) > GRID_ZLIM) return null; // via de acesso → não clampa
  let bx = x, bz = z, bd = Infinity;
  // NUNCA projeta para DENTRO de uma superquadra: lá é calçadão. A projeção estende a
  // via por meia-largura nas pontas (p/ cobrir o cruzamento), e numa via que MORRE no
  // anel — o toco de acesso — essa sobra cai no distrito. Sem esta recusa, o carro que
  // abre demais a curva à esquerda ao entrar no anel era "corrigido" para cima da
  // calçada em vez de ser trazido de volta para o asfalto.
  const consider = (px, pz) => {
    if (insideDistrict(px, pz)) return;
    const d = (px - x) ** 2 + (pz - z) ** 2;
    if (d < bd) { bd = d; bx = px; bz = pz; }
  };
  for (const r of DRIVE_V) {
    consider(
      clamp(x, r.x - DRIVE_HALF, r.x + DRIVE_HALF),
      clamp(z, r.zMin - ROAD_HALF, r.zMax + ROAD_HALF)
    );
  }
  for (const r of DRIVE_H) {
    consider(
      clamp(x, r.xMin - ROAD_HALF, r.xMax + ROAD_HALF),
      clamp(z, r.z - DRIVE_HALF, r.z + DRIVE_HALF)
    );
  }
  return bd > 1e-6 ? { x: bx, z: bz } : null;
}

/** Está num CRUZAMENTO (perto de um cruzamento vertical×horizontal)? */
function onIntersection(x, z) {
  let nearV = false;
  for (const r of DRIVE_V) { if (Math.abs(x - r.x) < ROAD_HALF) { nearV = true; break; } }
  if (!nearV) return false;
  for (const r of DRIVE_H) { if (Math.abs(z - r.z) < ROAD_HALF) return true; }
  return false;
}

/** Dentro da PISTA de rolamento (mais estrito que onRoad): pedestre realmente
 *  atravessando, não apenas na beira da calçada. */
const LANE_HALF = ROAD_HALF - 1.4;
function onRoadStrict(x, z) {
  for (const r of DRIVE_V) {
    if (Math.abs(x - r.x) < LANE_HALF && z > r.zMin - ROAD_HALF && z < r.zMax + ROAD_HALF) return true;
  }
  for (const r of DRIVE_H) {
    if (Math.abs(z - r.z) < LANE_HALF && x > r.xMin - ROAD_HALF && x < r.xMax + ROAD_HALF) return true;
  }
  return false;
}
